/**
 * Unified Property Management Page - HOTELS ON TAP
 * 
 * Single-page property management combining:
 * - Property Info (inline edit)
 * - Room Tags (create, assign, test) - INLINE
 * - Review Cards (create, configure URLs, test) - INLINE
 * - Survey Config - COLLAPSIBLE
 * - Analytics Preview - EMBEDDED
 * 
 * UX Principles:
 * - One primary flow per task
 * - Inline actions (no page hopping)
 * - Clear visual states: Active / Ready / Needs Attention
 * - Progressive disclosure (advanced settings hidden until needed)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hotelsAPI, roomsAPI, analyticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Building2,
  ArrowLeft,
  Loader2,
  Save,
  Plus,
  Edit,
  Trash2,
  DoorOpen,
  Tag,
  Activity,
  Star,
  ClipboardCheck,
  Link2,
  Settings,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  TrendingUp,
  Zap,
  AlertCircle,
  CheckCircle2,
  Clock,
  Eye,
  UserPlus,
  Users,
  Download,
  BarChart3,
  FileText,
  Droplets,
  Image,
  Upload,
  X,
  Globe,
  Send,
  CalendarClock,
  Smartphone,
  Bell,
  ShieldAlert,
  Pencil,
  Sparkles,
  Megaphone,
} from 'lucide-react';
import { SetupWizard } from '../../components/SetupWizard';
import EmployeeCodesManagement from './EmployeeCodesManagement';
import brandConfig from '../../config/brand';

// Status badge component
const StatusBadge = ({ status }) => {
  const configs = {
    active: { label: 'Active', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
    ready: { label: 'Ready', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    needs_attention: { label: 'Needs Setup', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    inactive: { label: 'Inactive', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  };
  const config = configs[status] || configs.inactive;
  return (
    <Badge variant="outline" className={`text-xs ${config.className}`}>
      {config.label}
    </Badge>
  );
};

// Quick stat card
const QuickStat = ({ icon: Icon, value, label, trend, color = 'blue' }) => (
  <div className="flex items-center space-x-3 p-3 rounded-lg bg-[hsl(217,19%,15%)]">
    <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
      <Icon className={`w-5 h-5 text-${color}-400`} />
    </div>
    <div>
      <p className="text-xl font-bold text-white">{value}</p>
      <p className="text-xs text-[hsl(215,20%,65%)]">{label}</p>
    </div>
    {trend && (
      <div className={`ml-auto text-xs ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
        {trend > 0 ? '+' : ''}{trend}%
      </div>
    )}
  </div>
);

export default function UnifiedPropertyPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Core data
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
  const [analyticsTab, setAnalyticsTab] = useState('reviews'); // 'reviews', 'surveys', 'restroom', or 'retailer_alert'
  
  // Survey Analytics state
  const [surveyAnalytics, setSurveyAnalytics] = useState(null);
  const [surveyAnalyticsLoading, setSurveyAnalyticsLoading] = useState(false);
  const [comparePeriod, setComparePeriod] = useState(null); // null, 7, 30, 90
  
  // Restroom Survey Analytics state
  const [restroomAnalytics, setRestroomAnalytics] = useState(null);
  const [restroomAnalyticsLoading, setRestroomAnalyticsLoading] = useState(false);
  
  // Section states (for collapsible sections)
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  const [roomsExpanded, setRoomsExpanded] = useState(true);
  const [reviewCardsExpanded, setReviewCardsExpanded] = useState(true);
  const [employeesExpanded, setEmployeesExpanded] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  
  // Edit states
  const [editingInfo, setEditingInfo] = useState(false);
  const [hotelForm, setHotelForm] = useState({});
  
  // Room dialog
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomForm, setRoomForm] = useState({ identifier: '', floor: '', room_type: 'standard' });
  
  // Employee management
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({ name: '', email: '', phone: '', role: 'staff' });
  
  // User-to-Property Assignment
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersExpanded, setUsersExpanded] = useState(false);
  const [assignUserDialogOpen, setAssignUserDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assignRole, setAssignRole] = useState('admin');
  
  // Language Settings
  const [roomDefaultLang, setRoomDefaultLang] = useState('en');
  const [restroomDefaultLang, setRestroomDefaultLang] = useState('en');
  const [roomLandingPageEnabled, setRoomLandingPageEnabled] = useState(false);
  const [restroomLandingPageEnabled, setRestroomLandingPageEnabled] = useState(false);
  const [landingWelcomeMessage, setLandingWelcomeMessage] = useState('Welcome / Bienvenue');
  const [landingShowPropertyName, setLandingShowPropertyName] = useState(true);
  
  // Review Redirect Settings (Google Review Redirect)
  const [roomReviewRedirectEnabled, setRoomReviewRedirectEnabled] = useState(false);
  const [roomReviewRedirectUrl, setRoomReviewRedirectUrl] = useState('');
  const [restroomReviewRedirectEnabled, setRestroomReviewRedirectEnabled] = useState(false);
  const [restroomReviewRedirectUrl, setRestroomReviewRedirectUrl] = useState('');
  const [retailerAlertReviewRedirectEnabled, setRetailerAlertReviewRedirectEnabled] = useState(false);
  const [retailerAlertReviewRedirectUrl, setRetailerAlertReviewRedirectUrl] = useState('');
  
  // Tag assignment (inline)
  const [assigningTagForRoom, setAssigningTagForRoom] = useState(null);
  const [tagId, setTagId] = useState('');
  const [roomTagLabel, setRoomTagLabel] = useState('');
  const [alertAssignmentType, setAlertAssignmentType] = useState('none'); // 'none', 'user', 'custom'
  const [alertUserId, setAlertUserId] = useState('');
  const [alertCustomEmail, setAlertCustomEmail] = useState('');
  
  // Restroom Tags state
  const [restroomTagsExpanded, setRestroomTagsExpanded] = useState(false);
  const [restroomLocations, setRestroomLocations] = useState([]);
  const [restroomLocationDialogOpen, setRestroomLocationDialogOpen] = useState(false);
  const [selectedRestroomLocation, setSelectedRestroomLocation] = useState(null);
  const [restroomLocationForm, setRestroomLocationForm] = useState({ name: '', floor: '', building: '' });
  const [assigningTagForRestroom, setAssigningTagForRestroom] = useState(null);
  const [restroomTagId, setRestroomTagId] = useState('');
  const [restroomTagLabel, setRestroomTagLabel] = useState('');
  
  // Retailer Alert Tags state
  const [retailerAlertTagsExpanded, setRetailerAlertTagsExpanded] = useState(false);
  const [retailerAlertLocations, setRetailerAlertLocations] = useState([]);
  const [retailerAlertLocationDialogOpen, setRetailerAlertLocationDialogOpen] = useState(false);
  const [selectedRetailerAlertLocation, setSelectedRetailerAlertLocation] = useState(null);
  const [retailerAlertLocationForm, setRetailerAlertLocationForm] = useState({ name: '', floor: '', building: '' });
  const [assigningTagForRetailerAlert, setAssigningTagForRetailerAlert] = useState(null);
  const [retailerAlertTagId, setRetailerAlertTagId] = useState('');
  const [retailerAlertTagLabel, setRetailerAlertTagLabel] = useState('');
  
  // Retailer Alert Analytics state
  const [retailerAlertAnalytics, setRetailerAlertAnalytics] = useState(null);
  const [retailerAlertAnalyticsLoading, setRetailerAlertAnalyticsLoading] = useState(false);
  const [retailerAlertEnabled, setRetailerAlertEnabled] = useState(false);
  
  // Retailer Alert Language Settings
  const [retailerAlertDefaultLang, setRetailerAlertDefaultLang] = useState('en');
  const [retailerAlertLandingPageEnabled, setRetailerAlertLandingPageEnabled] = useState(false);
  
  // Review URL states
  const [reviewUrls, setReviewUrls] = useState({
    google: '', tripadvisor: '', yelp: '', facebook: '', trustpilot: ''
  });
  const [defaultPlatform, setDefaultPlatform] = useState('google');
  const [surveyEnabled, setSurveyEnabled] = useState(false);
  const [restroomSurveyEnabled, setRestroomSurveyEnabled] = useState(false);
  const [reviewsEnabled, setReviewsEnabled] = useState(false);
  
  // Main Logo states (in Property Info)
  const [mainLogoUrl, setMainLogoUrl] = useState('');
  const [mainLogoSize, setMainLogoSize] = useState('medium');
  const [mainLogoEnabled, setMainLogoEnabled] = useState(false);
  const [uploadingMainLogo, setUploadingMainLogo] = useState(false);
  const [mainLogoUrlInput, setMainLogoUrlInput] = useState('');

  // Communications Engine states (Pyramid 3)
  const [commsConfig, setCommsConfig] = useState({
    engine_enabled: false, allow_scheduling: true, allow_images: true,
    allow_price_field: false, allow_link_url: true,
    default_message_scope: 'tag', require_expiration: false, max_active_messages: 10,
  });
  const [commsExpanded, setCommsExpanded] = useState(false);
  
  // Survey Branding states (Advanced Settings - for card-level overrides)
  const [brandingExpanded, setBrandingExpanded] = useState(false);
  const [propertyLogoUrl, setPropertyLogoUrl] = useState('');
  const [propertyLogoSize, setPropertyLogoSize] = useState('medium');
  const [usePropertyLogoForAll, setUsePropertyLogoForAll] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUrlInput, setLogoUrlInput] = useState('');
  
  // Copied state for feedback
  const [copiedId, setCopiedId] = useState(null);
  
  // Summary Email Settings
  const [summaryEmailEnabled, setSummaryEmailEnabled] = useState(false);
  const [summaryEmailFrequency, setSummaryEmailFrequency] = useState('daily');
  const [summaryEmailRecipients, setSummaryEmailRecipients] = useState('');
  const [summaryEmailSaving, setSummaryEmailSaving] = useState(false);
  const [summaryEmailSending, setSummaryEmailSending] = useState(false);
  const [summaryEmailLastSent, setSummaryEmailLastSent] = useState(null);
  
  // Notification Settings (SMS + Escalation)
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalationWindow, setEscalationWindow] = useState(5);
  const [alertMethod, setAlertMethod] = useState("both");
  const [resolutionTrackingEnabled, setResolutionTrackingEnabled] = useState(false);
  const [archiveEmail, setArchiveEmail] = useState('');
  const [autoCloseMinutes, setAutoCloseMinutes] = useState(20);
  const [alertStatusFilter, setAlertStatusFilter] = useState("all");
  const [notifSettingsSaving, setNotifSettingsSaving] = useState(false);
  
  // Alert Recipients
  const [alertRecipients, setAlertRecipients] = useState([]);
  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [recipientForm, setRecipientForm] = useState({ name: '', type: 'email', address: '', phone_number: '', carrier: '', is_escalation: false });
  const [recipientSaving, setRecipientSaving] = useState(false);
  const [carrierList, setCarrierList] = useState([]);
  
  // Alert History
  const [alertNotifications, setAlertNotifications] = useState([]);
  const [alertHistoryExpanded, setAlertHistoryExpanded] = useState(false);
  
  // Delete hotel dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Setup Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('hotels_token') || localStorage.getItem('token');

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [hotelRes, roomsRes, usersRes] = await Promise.all([
        hotelsAPI.get(id),
        roomsAPI.list(id),
        hotelsAPI.getUsers(id).catch(() => ({ data: { users: [] } })),
      ]);
      
      const hotelData = hotelRes.data;
      setHotel(hotelData);
      setRooms(roomsRes.data || []);
      setUsers(usersRes.data?.users || []);
      
      // Fetch employees
      try {
        const empRes = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/employees`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (empRes.ok) {
          const empData = await empRes.json();
          setEmployees(empData || []);
        }
      } catch (e) {
        console.log('Employees not loaded');
      }
      
      // Fetch restroom locations
      try {
        const restroomRes = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/restroom-locations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (restroomRes.ok) {
          const restroomData = await restroomRes.json();
          setRestroomLocations(restroomData || []);
        }
      } catch (e) {
        console.log('Restroom locations not loaded');
      }
      
      // Fetch retailer alert locations
      try {
        const retailerRes = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/retailer-alert-locations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (retailerRes.ok) {
          const retailerData = await retailerRes.json();
          setRetailerAlertLocations(retailerData || []);
        }
      } catch (e) {
        console.log('Retailer alert locations not loaded');
      }
      
      // Fetch assigned users
      try {
        const usersRes2 = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/assigned-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (usersRes2.ok) {
          setAssignedUsers(await usersRes2.json() || []);
        }
      } catch (e) {
        console.log('Assigned users not loaded');
      }
      
      // Set form data
      setHotelForm({
        name: hotelData.name || '',
        hotel_code: hotelData.hotel_code || '',
        address: hotelData.address || '',
        city: hotelData.city || '',
        contact_email: hotelData.contact_email || '',
        contact_phone: hotelData.contact_phone || '',
      });
      
      // Set engagement modes
      setSurveyEnabled(hotelData.engagement_modes?.in_room_survey?.enabled || false);
      setRestroomSurveyEnabled(hotelData.engagement_modes?.restroom_survey?.enabled || false);
      setRetailerAlertEnabled(hotelData.engagement_modes?.retailer_alert?.enabled || false);
      setReviewsEnabled(hotelData.engagement_modes?.reviews?.enabled || false);
      
      // Set review URLs
      const reviewsConfig = hotelData.engagement_modes?.reviews || {};
      const tapEngineConfig = hotelData.engagement_modes?.tap_engine_urls || {};
      setReviewUrls({
        google: reviewsConfig.google_url || tapEngineConfig.google_reviews_url || '',
        tripadvisor: reviewsConfig.tripadvisor_url || tapEngineConfig.tripadvisor_url || '',
        yelp: reviewsConfig.yelp_url || tapEngineConfig.yelp_url || '',
        facebook: reviewsConfig.facebook_url || tapEngineConfig.facebook_url || '',
        trustpilot: reviewsConfig.trustpilot_url || tapEngineConfig.trustpilot_url || '',
      });
      setDefaultPlatform(tapEngineConfig.default_platform || reviewsConfig.default_platform || 'google');
      
      // Set language settings
      const langSettings = hotelData.language_settings || {};
      setRoomDefaultLang(langSettings.room_default_lang || 'en');
      setRestroomDefaultLang(langSettings.restroom_default_lang || 'en');
      setRoomLandingPageEnabled(langSettings.room_landing_page || false);
      setRestroomLandingPageEnabled(langSettings.restroom_landing_page || false);
      setLandingWelcomeMessage(langSettings.welcome_message || 'Welcome / Bienvenue');
      setLandingShowPropertyName(langSettings.show_property_name !== false);
      
      // Set review redirect settings
      const reviewRedirect = hotelData.review_redirect || {};
      setRoomReviewRedirectEnabled(reviewRedirect.room_enabled || false);
      setRoomReviewRedirectUrl(reviewRedirect.room_url || '');
      setRestroomReviewRedirectEnabled(reviewRedirect.restroom_enabled || false);
      setRestroomReviewRedirectUrl(reviewRedirect.restroom_url || '');
      setRetailerAlertReviewRedirectEnabled(reviewRedirect.retailer_alert_enabled || false);
      setRetailerAlertReviewRedirectUrl(reviewRedirect.retailer_alert_url || '');
      
      // Set retailer alert language settings
      setRetailerAlertDefaultLang(langSettings.retailer_alert_default_lang || 'en');
      setRetailerAlertLandingPageEnabled(langSettings.retailer_alert_landing_page || false);
      
      // Set main logo (from Property Info)
      const mainLogo = hotelData.main_logo || {};
      setMainLogoUrl(mainLogo.url || '');
      setMainLogoSize(mainLogo.size || 'medium');
      setMainLogoEnabled(mainLogo.enabled || false);
      
      // Set survey branding (Advanced Settings - for card-level overrides)
      const branding = hotelData.survey_branding || {};
      setPropertyLogoUrl(branding.property_logo_url || '');
      setPropertyLogoSize(branding.property_logo_size || 'medium');
      setUsePropertyLogoForAll(branding.use_property_logo_for_all || false);
      
      // Load summary email settings
      const summaryEmail = hotelData.summary_email || {};
      setSummaryEmailEnabled(summaryEmail.enabled || false);
      setSummaryEmailFrequency(summaryEmail.frequency || 'daily');
      setSummaryEmailRecipients((summaryEmail.recipients || []).join(', '));
      setSummaryEmailLastSent(summaryEmail.last_sent || null);
      
      // Load notification settings (SMS + escalation)
      const notifSettings = hotelData.notification_settings || {};
      setSmsEnabled(notifSettings.sms_enabled || false);
      setEscalationEnabled(notifSettings.escalation_enabled || false);
      setEscalationWindow(notifSettings.escalation_window_minutes || 5);
      setAlertMethod(notifSettings.alert_method || "both");
      setResolutionTrackingEnabled(notifSettings.resolution_tracking_enabled || false);
      setAutoCloseMinutes(notifSettings.auto_close_minutes || 20);
      setArchiveEmail(notifSettings.archive_email || '');
      
      // Load alert recipients
      try {
        const recipientsRes = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/alert-recipients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (recipientsRes.ok) setAlertRecipients(await recipientsRes.json());
      } catch (e) { console.log('Alert recipients not loaded'); }
      
      // Load carriers list
      try {
        const carriersRes = await fetch(`${API_BASE}/api/hotels-standalone/carriers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (carriersRes.ok) setCarrierList(await carriersRes.json());
      } catch (e) { console.log('Carriers not loaded'); }
      
      // Load alert notifications
      try {
        const alertsRes = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/alert-notifications?limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (alertsRes.ok) setAlertNotifications(await alertsRes.json());
      } catch (e) { console.log('Alert history not loaded'); }
      
      // Fetch stats
      try {
        const statsRes = await analyticsAPI.getOverview(id, 7).catch(() => hotelsAPI.getStats(id));
        setStats(statsRes.data);
      } catch (e) {
        console.log('Stats not available');
      }

      // Fetch Communications Engine config
      try {
        const commsRes = await fetch(`${API_BASE}/api/communications/config/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (commsRes.ok) {
          setCommsConfig(await commsRes.json());
        }
      } catch (e) {
        console.log('Communications config not loaded');
      }
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/hotels');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save hotel info
  const saveHotelInfo = async () => {
    setSaving(true);
    try {
      await hotelsAPI.update(id, hotelForm);
      setHotel({ ...hotel, ...hotelForm });
      setEditingInfo(false);
      toast.success('Property info updated');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Fetch analytics data
  const fetchAnalytics = async (period = analyticsPeriod) => {
    if (!id) return;
    setAnalyticsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/hotels-standalone/analytics/hotel/${id}?days=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        console.error('Failed to fetch analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch survey analytics data
  const fetchSurveyAnalytics = async (period = analyticsPeriod, compare = comparePeriod) => {
    if (!id) return;
    setSurveyAnalyticsLoading(true);
    try {
      let url = `${API_BASE}/api/hotels-standalone/analytics/hotel/${id}/surveys?days=${period}`;
      if (compare) {
        url += `&compare_days=${compare}`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSurveyAnalytics(data);
      } else {
        console.error('Failed to fetch survey analytics');
      }
    } catch (error) {
      console.error('Error fetching survey analytics:', error);
    } finally {
      setSurveyAnalyticsLoading(false);
    }
  };

  // Export analytics to CSV - uses per-module endpoints
  const exportAnalytics = async (moduleType) => {
    const endpointMap = {
      reviews: `/api/hotels-standalone/organizations/${id}/export/reviews`,
      surveys: `/api/hotels-standalone/organizations/${id}/export/room-surveys`,
      restroom: `/api/hotels-standalone/organizations/${id}/export/restroom-surveys`,
      retailer_alert: `/api/hotels-standalone/organizations/${id}/export/retailer-alerts`,
    };
    const filenameMap = {
      reviews: 'review_taps',
      surveys: 'room_surveys',
      restroom: 'restroom_surveys',
      retailer_alert: 'retailer_alerts',
    };
    const endpoint = endpointMap[moduleType] || endpointMap.reviews;
    const url = `${API_BASE}${endpoint}`;
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${filenameMap[moduleType] || 'analytics'}_export.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('CSV exported successfully');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  // Auto-load analytics when section is expanded
  useEffect(() => {
    if (analyticsExpanded && !analyticsData && !analyticsLoading && id) {
      fetchAnalytics();
    }
  }, [analyticsExpanded, id]);

  // Load survey analytics when tab switches
  useEffect(() => {
    if (analyticsExpanded && analyticsTab === 'surveys' && !surveyAnalytics && !surveyAnalyticsLoading && id) {
      fetchSurveyAnalytics();
    }
  }, [analyticsExpanded, analyticsTab, id]);

  // Load restroom analytics when tab switches
  useEffect(() => {
    if (analyticsExpanded && analyticsTab === 'restroom' && !restroomAnalytics && !restroomAnalyticsLoading && id) {
      fetchRestroomAnalytics();
    }
  }, [analyticsExpanded, analyticsTab, id]);

  // Fetch restroom survey analytics
  const fetchRestroomAnalytics = async () => {
    if (!id) return;
    setRestroomAnalyticsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/restroom-analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRestroomAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching restroom analytics:', error);
    } finally {
      setRestroomAnalyticsLoading(false);
    }
  };

  // Load retailer alert analytics when tab switches
  useEffect(() => {
    if (analyticsExpanded && analyticsTab === 'retailer_alert' && !retailerAlertAnalytics && !retailerAlertAnalyticsLoading && id) {
      fetchRetailerAlertAnalytics();
    }
  }, [analyticsExpanded, analyticsTab, id]);

  // Fetch retailer alert analytics
  const fetchRetailerAlertAnalytics = async () => {
    if (!id) return;
    setRetailerAlertAnalyticsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/retailer-alert-analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRetailerAlertAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching retailer alert analytics:', error);
    } finally {
      setRetailerAlertAnalyticsLoading(false);
    }
  };

  // Save engagement settings
  const saveEngagementSettings = async () => {
    setSaving(true);
    try {
      const reviewsUpdate = { enabled: reviewsEnabled, default_platform: defaultPlatform };
      const tapEngineUpdate = { default_platform: defaultPlatform };
      
      if (reviewUrls.google?.trim()) {
        reviewsUpdate.google_url = reviewUrls.google;
        tapEngineUpdate.google_reviews_url = reviewUrls.google;
      }
      if (reviewUrls.tripadvisor?.trim()) {
        reviewsUpdate.tripadvisor_url = reviewUrls.tripadvisor;
        tapEngineUpdate.tripadvisor_url = reviewUrls.tripadvisor;
      }
      if (reviewUrls.yelp?.trim()) {
        reviewsUpdate.yelp_url = reviewUrls.yelp;
        tapEngineUpdate.yelp_url = reviewUrls.yelp;
      }
      if (reviewUrls.facebook?.trim()) {
        reviewsUpdate.facebook_url = reviewUrls.facebook;
        tapEngineUpdate.facebook_url = reviewUrls.facebook;
      }
      if (reviewUrls.trustpilot?.trim()) {
        reviewsUpdate.trustpilot_url = reviewUrls.trustpilot;
        tapEngineUpdate.trustpilot_url = reviewUrls.trustpilot;
      }
      
      await hotelsAPI.update(id, {
        engagement_modes: {
          in_room_survey: { enabled: surveyEnabled },
          restroom_survey: { enabled: restroomSurveyEnabled },
          retailer_alert: { enabled: retailerAlertEnabled },
          reviews: reviewsUpdate,
          tap_engine_urls: tapEngineUpdate,
        },
        language_settings: {
          room_default_lang: roomDefaultLang,
          restroom_default_lang: restroomDefaultLang,
          retailer_alert_default_lang: retailerAlertDefaultLang,
          room_landing_page: roomLandingPageEnabled,
          restroom_landing_page: restroomLandingPageEnabled,
          retailer_alert_landing_page: retailerAlertLandingPageEnabled,
          welcome_message: landingWelcomeMessage,
          show_property_name: landingShowPropertyName,
        },
        review_redirect: {
          room_enabled: roomReviewRedirectEnabled,
          room_url: roomReviewRedirectUrl.trim(),
          restroom_enabled: restroomReviewRedirectEnabled,
          restroom_url: restroomReviewRedirectUrl.trim(),
          retailer_alert_enabled: retailerAlertReviewRedirectEnabled,
          retailer_alert_url: retailerAlertReviewRedirectUrl.trim(),
        },
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG image');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_BASE}/api/hotels-standalone/upload/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!res.ok) throw new Error('Upload failed');
      
      const data = await res.json();
      setPropertyLogoUrl(data.url);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle URL paste for logo
  const handleLogoUrlPaste = () => {
    if (logoUrlInput.trim()) {
      setPropertyLogoUrl(logoUrlInput.trim());
      setLogoUrlInput('');
      toast.success('Logo URL set');
    }
  };

  // Save branding settings
  const saveBrandingSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/survey-branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          property_logo_url: propertyLogoUrl || null,
          property_logo_size: propertyLogoSize,
          use_property_logo_for_all: usePropertyLogoForAll
        })
      });
      
      if (!res.ok) throw new Error('Failed to save');
      
      toast.success('Branding settings saved');
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  // Remove logo
  const removeLogo = () => {
    setPropertyLogoUrl('');
  };

  // ==========================================
  // SUMMARY EMAIL HANDLERS
  // ==========================================
  
  const saveSummaryEmailSettings = async () => {
    setSummaryEmailSaving(true);
    try {
      const recipientsList = summaryEmailRecipients
        .split(',')
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));
      
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/summary-email-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          enabled: summaryEmailEnabled,
          frequency: summaryEmailFrequency,
          recipients: recipientsList,
        })
      });
      if (res.ok) {
        toast.success('Summary email settings saved');
      } else {
        toast.error('Failed to save summary email settings');
      }
    } catch (error) {
      console.error('Error saving summary email settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSummaryEmailSaving(false);
    }
  };

  const sendTestSummaryEmail = async () => {
    setSummaryEmailSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/send-summary-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setSummaryEmailLastSent(new Date().toISOString());
        toast.success(`Summary email sent to ${data.recipients?.length || 0} recipient(s)`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send summary email');
      }
    } catch (error) {
      console.error('Error sending summary email:', error);
      toast.error('Failed to send summary email');
    } finally {
      setSummaryEmailSending(false);
    }
  };

  // ==========================================
  // NOTIFICATION SETTINGS HANDLERS (SMS + Escalation)
  // ==========================================
  
  const saveNotificationSettings = async () => {
    setNotifSettingsSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/notification-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          sms_enabled: smsEnabled,
          escalation_enabled: escalationEnabled,
          escalation_window_minutes: escalationWindow,
          alert_method: alertMethod,
          resolution_tracking_enabled: resolutionTrackingEnabled,
          auto_close_minutes: autoCloseMinutes,
          archive_email: archiveEmail,
        })
      });
      if (res.ok) toast.success('Notification settings saved');
      else toast.error('Failed to save notification settings');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setNotifSettingsSaving(false);
    }
  };

  // ==========================================
  // ALERT RECIPIENT HANDLERS
  // ==========================================
  
  const fetchRecipients = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/alert-recipients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAlertRecipients(await res.json());
    } catch (e) { console.error(e); }
  };

  const openRecipientDialog = (recipient = null) => {
    if (recipient) {
      setEditingRecipient(recipient);
      setRecipientForm({
        name: recipient.name || '',
        type: recipient.type || 'email',
        address: recipient.address || '',
        phone_number: recipient.phone_number || '',
        carrier: recipient.carrier || '',
        is_escalation: recipient.is_escalation || false,
      });
    } else {
      setEditingRecipient(null);
      setRecipientForm({ name: '', type: 'email', address: '', phone_number: '', carrier: '', is_escalation: false });
    }
    setRecipientDialogOpen(true);
  };

  const saveRecipient = async () => {
    if (recipientForm.type === 'sms') {
      if (!recipientForm.phone_number || !recipientForm.carrier) {
        toast.error('Phone number and carrier are required');
        return;
      }
    } else {
      if (!recipientForm.address || !recipientForm.address.includes('@')) {
        toast.error('Valid email address is required');
        return;
      }
    }
    setRecipientSaving(true);
    try {
      const url = editingRecipient
        ? `${API_BASE}/api/hotels-standalone/organizations/${id}/alert-recipients/${editingRecipient.id}`
        : `${API_BASE}/api/hotels-standalone/organizations/${id}/alert-recipients`;
      const res = await fetch(url, {
        method: editingRecipient ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(recipientForm)
      });
      if (res.ok) {
        toast.success(editingRecipient ? 'Recipient updated' : 'Recipient added');
        setRecipientDialogOpen(false);
        await fetchRecipients();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save recipient');
      }
    } catch (error) {
      toast.error('Failed to save recipient');
    } finally {
      setRecipientSaving(false);
    }
  };

  const deleteRecipient = async (recipientId) => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/alert-recipients/${recipientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Recipient removed');
        await fetchRecipients();
      } else toast.error('Failed to remove recipient');
    } catch (error) {
      toast.error('Failed to remove recipient');
    }
  };

  const fetchAlertHistory = async (statusFilter) => {
    try {
      const status = statusFilter || alertStatusFilter || "all";
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/alert-notifications?limit=50&status=${status}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAlertNotifications(await res.json());
    } catch (e) { console.error(e); }
  };

  // ==========================================
  // MAIN LOGO HANDLERS (Property Info section)
  // ==========================================
  
  // Handle main logo file upload
  const handleMainLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingMainLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_BASE}/api/hotels-standalone/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) throw new Error('Upload failed');
      
      const data = await res.json();
      setMainLogoUrl(data.url);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingMainLogo(false);
    }
  };

  // Handle URL paste for main logo
  const handleMainLogoUrlPaste = () => {
    if (mainLogoUrlInput.trim()) {
      setMainLogoUrl(mainLogoUrlInput.trim());
      setMainLogoUrlInput('');
      toast.success('Logo URL set');
    }
  };

  // Save main logo settings
  const saveMainLogo = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/main-logo`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url: mainLogoUrl || null,
          size: mainLogoSize,
          enabled: mainLogoEnabled
        })
      });
      
      if (!res.ok) throw new Error('Failed to save');
      
      toast.success('Main logo saved');
    } catch (error) {
      toast.error('Failed to save main logo');
    } finally {
      setSaving(false);
    }
  };

  // Room operations
  const openRoomDialog = (room = null) => {
    setSelectedRoom(room);
    setRoomForm(room ? {
      identifier: room.identifier,
      floor: room.floor || '',
      room_type: room.room_type || 'standard',
    } : { identifier: '', floor: '', room_type: 'standard' });
    setRoomDialogOpen(true);
  };

  const saveRoom = async () => {
    if (!roomForm.identifier.trim()) {
      toast.error('Room number is required');
      return;
    }
    setSaving(true);
    try {
      if (selectedRoom) {
        await roomsAPI.update(id, selectedRoom.id, roomForm);
        toast.success('Room updated');
      } else {
        await roomsAPI.create(id, roomForm);
        toast.success('Room created');
      }
      setRoomDialogOpen(false);
      const roomsRes = await roomsAPI.list(id);
      setRooms(roomsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save room');
    } finally {
      setSaving(false);
    }
  };

  const deleteRoom = async (roomId) => {
    if (!window.confirm('Delete this room?')) return;
    try {
      await roomsAPI.delete(id, roomId);
      setRooms(rooms.filter(r => r.id !== roomId));
      toast.success('Room deleted');
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  // Employee operations
  const openEmployeeDialog = (employee = null) => {
    setSelectedEmployee(employee);
    setEmployeeForm(employee ? {
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      role: employee.role || 'staff'
    } : { name: '', email: '', phone: '', role: 'staff' });
    setEmployeeDialogOpen(true);
  };

  const saveEmployee = async () => {
    if (!employeeForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!employeeForm.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setSaving(true);
    try {
      if (selectedEmployee) {
        // Update existing employee
        const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/employees/${selectedEmployee.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(employeeForm)
        });
        if (!res.ok) throw new Error('Failed to update');
        const updated = await res.json();
        setEmployees(employees.map(e => e.id === selectedEmployee.id ? updated : e));
        toast.success('Employee updated');
      } else {
        // Create new employee
        const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/employees`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(employeeForm)
        });
        if (!res.ok) throw new Error('Failed to create');
        const created = await res.json();
        setEmployees([...employees, created]);
        toast.success('Employee added');
      }
      setEmployeeDialogOpen(false);
    } catch (error) {
      toast.error(error.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const deleteEmployee = async (employeeId) => {
    if (!window.confirm('Delete this employee?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/employees/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      setEmployees(employees.filter(e => e.id !== employeeId));
      toast.success('Employee deleted');
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  // User-to-Property Assignment operations
  const fetchAllUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/users/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAllUsers(await res.json());
    } catch (e) {
      console.log('Failed to fetch users');
    }
  };

  const assignUser = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/assign-user`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedUserId, role: assignRole })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to assign user');
      }
      const data = await res.json();
      setAssignedUsers([...assignedUsers, data]);
      setAssignUserDialogOpen(false);
      setSelectedUserId('');
      setAssignRole('admin');
      toast.success('User assigned to property');
    } catch (error) {
      toast.error(error.message || 'Failed to assign user');
    }
  };

  const unassignUser = async (userId) => {
    if (!window.confirm('Remove this user from this property?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/unassign-user/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to unassign');
      setAssignedUsers(assignedUsers.filter(a => a.user_id !== userId));
      toast.success('User unassigned from property');
    } catch (error) {
      toast.error('Failed to unassign user');
    }
  };

  // Tag operations (inline)
  const startAssignTag = (room) => {
    setAssigningTagForRoom(room);
    setTagId(room.tag_id || '');
    setRoomTagLabel(room.tag_label || '');
    // Load existing alert settings
    setAlertAssignmentType(
      room.alert_user_id ? 'user' : 
      room.alert_email ? 'custom' : 'none'
    );
    setAlertUserId(room.alert_user_id || '');
    setAlertCustomEmail(room.alert_email || '');
  };

  const assignTag = async () => {
    if (!tagId.trim()) {
      toast.error('Tag ID is required');
      return;
    }
    setSaving(true);
    try {
      let cleanTagId = tagId.trim();
      if (hotel?.hotel_code && cleanTagId.toUpperCase().startsWith(hotel.hotel_code.toUpperCase() + '-')) {
        cleanTagId = cleanTagId.substring(hotel.hotel_code.length + 1);
      }
      const fullTagId = hotel?.hotel_code ? `${hotel.hotel_code}-${cleanTagId}` : cleanTagId;
      
      // Determine alert settings to send
      let alertUserIdToSend = null;
      let alertEmailToSend = null;
      if (alertAssignmentType === 'user' && alertUserId) {
        alertUserIdToSend = alertUserId;
      } else if (alertAssignmentType === 'custom' && alertCustomEmail.trim()) {
        alertEmailToSend = alertCustomEmail.trim();
      }
      
      await roomsAPI.assignTag(id, assigningTagForRoom.id, fullTagId, 'in_room_survey', alertUserIdToSend, alertEmailToSend, roomTagLabel.trim() || null);
      toast.success('Tag assigned!');
      setAssigningTagForRoom(null);
      // Reset alert state
      setAlertAssignmentType('none');
      setAlertUserId('');
      setAlertCustomEmail('');
      setRoomTagLabel('');
      const roomsRes = await roomsAPI.list(id);
      setRooms(roomsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign tag');
    } finally {
      setSaving(false);
    }
  };

  const unassignTag = async (room) => {
    if (!window.confirm('Remove tag from this room?')) return;
    try {
      await roomsAPI.unassignTag(id, room.id);
      const roomsRes = await roomsAPI.list(id);
      setRooms(roomsRes.data || []);
      toast.success('Tag removed');
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  // ==========================================
  // RESTROOM LOCATION & TAG OPERATIONS
  // ==========================================
  
  const openRestroomLocationDialog = (location = null) => {
    setSelectedRestroomLocation(location);
    setRestroomLocationForm(location ? {
      name: location.name || '',
      floor: location.floor || '',
      building: location.building || '',
    } : { name: '', floor: '', building: '' });
    setRestroomLocationDialogOpen(true);
  };

  const saveRestroomLocation = async () => {
    if (!restroomLocationForm.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    setSaving(true);
    try {
      if (selectedRestroomLocation) {
        // Update existing
        const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/restroom-locations/${selectedRestroomLocation.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(restroomLocationForm)
        });
        if (!res.ok) throw new Error('Failed to update');
        const updated = await res.json();
        setRestroomLocations(restroomLocations.map(l => l.id === selectedRestroomLocation.id ? updated : l));
        toast.success('Location updated');
      } else {
        // Create new
        const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/restroom-locations`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(restroomLocationForm)
        });
        if (!res.ok) throw new Error('Failed to create');
        const created = await res.json();
        setRestroomLocations([...restroomLocations, created]);
        toast.success('Location added');
      }
      setRestroomLocationDialogOpen(false);
    } catch (error) {
      toast.error(error.message || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const deleteRestroomLocation = async (locationId) => {
    if (!window.confirm('Delete this restroom location?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/restroom-locations/${locationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      setRestroomLocations(restroomLocations.filter(l => l.id !== locationId));
      toast.success('Location deleted');
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const startAssignRestroomTag = (location) => {
    setAssigningTagForRestroom(location.id);
    setRestroomTagId(location.tag_id || '');
    setRestroomTagLabel(location.tag_label || '');
  };

  const assignRestroomTag = async (location) => {
    if (!restroomTagId.trim()) {
      toast.error('Tag ID is required');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/restroom-locations/${location.id}/assign-tag`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tag_id: restroomTagId.trim(), tag_label: restroomTagLabel.trim() || null })
      });
      if (!res.ok) throw new Error('Failed to assign');
      const updated = await res.json();
      setRestroomLocations(restroomLocations.map(l => l.id === location.id ? updated : l));
      setAssigningTagForRestroom(null);
      setRestroomTagId('');
      setRestroomTagLabel('');
      toast.success('Tag assigned');
    } catch (error) {
      toast.error('Failed to assign tag');
    }
  };

  const unassignRestroomTag = async (location) => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/restroom-locations/${location.id}/unassign-tag`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to unassign');
      setRestroomLocations(restroomLocations.map(l => l.id === location.id ? { ...l, tag_id: null } : l));
      toast.success('Tag removed');
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  // ========================
  // RETAILER ALERT Location CRUD
  // ========================
  const openRetailerAlertLocationDialog = (location = null) => {
    setSelectedRetailerAlertLocation(location);
    setRetailerAlertLocationForm(location ? { name: location.name || '', floor: location.floor || '', building: location.building || '', zone_number: location.zone_number || '1' } : { name: '', floor: '', building: '', zone_number: '1' });
    setRetailerAlertLocationDialogOpen(true);
  };

  const saveRetailerAlertLocation = async () => {
    setSaving(true);
    try {
      if (selectedRetailerAlertLocation) {
        const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/retailer-alert-locations/${selectedRetailerAlertLocation.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(retailerAlertLocationForm)
        });
        if (!res.ok) throw new Error('Failed to update');
        const updated = await res.json();
        setRetailerAlertLocations(retailerAlertLocations.map(l => l.id === updated.id ? updated : l));
        toast.success('Location updated');
      } else {
        const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/retailer-alert-locations`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(retailerAlertLocationForm)
        });
        if (!res.ok) throw new Error('Failed to create');
        const created = await res.json();
        setRetailerAlertLocations([...retailerAlertLocations, created]);
        toast.success('Location added');
      }
      setRetailerAlertLocationDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const deleteRetailerAlertLocation = async (locationId) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/retailer-alert-locations/${locationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      setRetailerAlertLocations(retailerAlertLocations.filter(l => l.id !== locationId));
      toast.success('Location deleted');
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const startAssignRetailerAlertTag = (location) => {
    setAssigningTagForRetailerAlert(location.id);
    setRetailerAlertTagId(location.tag_id || '');
    setRetailerAlertTagLabel(location.tag_label || '');
  };

  const assignRetailerAlertTag = async (location) => {
    if (!retailerAlertTagId.trim()) {
      toast.error('Tag ID is required');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/retailer-alert-locations/${location.id}/assign-tag`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag_id: retailerAlertTagId.trim(), tag_label: retailerAlertTagLabel.trim() || null })
      });
      if (!res.ok) throw new Error('Failed to assign');
      const updated = await res.json();
      setRetailerAlertLocations(retailerAlertLocations.map(l => l.id === location.id ? updated : l));
      setAssigningTagForRetailerAlert(null);
      setRetailerAlertTagId('');
      setRetailerAlertTagLabel('');
      toast.success('Tag assigned');
    } catch (error) {
      toast.error('Failed to assign tag');
    }
  };

  const unassignRetailerAlertTag = async (location) => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${id}/retailer-alert-locations/${location.id}/unassign-tag`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to unassign');
      setRetailerAlertLocations(retailerAlertLocations.map(l => l.id === location.id ? { ...l, tag_id: null } : l));
      toast.success('Tag removed');
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  // Copy to clipboard with feedback
  const copyToClipboard = (text, itemId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(itemId);
    toast.success('Copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Delete hotel
  const deleteHotel = async () => {
    if (deleteConfirmText !== hotel.name) {
      toast.error('Please type the property name exactly to confirm');
      return;
    }
    setDeleting(true);
    try {
      await hotelsAPI.delete(id);
      toast.success('Property deleted');
      navigate('/hotels');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete property');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate property health
  const getPropertyHealth = () => {
    const roomsWithTags = rooms.filter(r => r.tag_id).length;
    const hasReviewUrls = Object.values(reviewUrls).some(u => u?.trim());
    
    if (rooms.length === 0) return { status: 'needs_attention', message: 'Add rooms to get started' };
    if (roomsWithTags === 0 && !hasReviewUrls) return { status: 'needs_attention', message: 'Set up tags or review URLs' };
    if (roomsWithTags === rooms.length && hasReviewUrls) return { status: 'active', message: 'Fully configured' };
    return { status: 'ready', message: `${roomsWithTags}/${rooms.length} rooms tagged` };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(222,47%,11%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!hotel) return null;

  const propertyHealth = getPropertyHealth();
  const configuredUrlCount = Object.values(reviewUrls).filter(url => url?.trim()).length;

  return (
    <div className="min-h-screen bg-[hsl(222,47%,11%)]">
      {/* Header - Sticky with context */}
      <header className="border-b border-[hsl(217,19%,27%)] bg-[hsl(217,19%,12%)] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/hotels')}
                className="flex items-center space-x-2 text-[hsl(215,20%,65%)] hover:text-white transition-colors"
                data-testid="back-to-hotels"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">All Properties</span>
              </button>
              <div className="h-5 w-px bg-[hsl(217,19%,27%)]" />
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h1 className="text-base font-semibold text-white">{hotel.name}</h1>
                    <StatusBadge status={propertyHealth.status} />
                  </div>
                  {hotel.hotel_code && (
                    <p className="text-xs text-blue-400 font-mono">{hotel.hotel_code}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/hotels/incidents?store=${id}`)}
                className="hidden sm:flex border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                data-testid="property-incident-data-btn"
              >
                <ShieldAlert className="w-4 h-4 mr-1" />
                Incident Data
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setWizardOpen(true)}
                className="hidden sm:flex border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                data-testid="guided-setup-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Guided Setup
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(`${PUBLIC_URL}/standalone/room/test`, '_blank')}
                className="hidden sm:flex border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)]"
              >
                <Eye className="w-4 h-4 mr-1" />
                Preview
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <QuickStat icon={DoorOpen} value={rooms.length} label="Rooms" color="blue" />
          <QuickStat icon={Tag} value={rooms.filter(r => r.tag_id).length} label="Tags Active" color="green" />
          <QuickStat icon={Activity} value={stats?.total_taps || 0} label="Total Taps" color="purple" />
          <QuickStat icon={Star} value={configuredUrlCount} label="Review Platforms" color="amber" />
        </div>

        {/* Guided Setup Banner — shows when property needs configuration */}
        {propertyHealth.status === 'needs_attention' && (
          <div
            className="flex items-center justify-between p-4 rounded-lg bg-blue-500/[0.06] border border-blue-500/20 cursor-pointer hover:bg-blue-500/[0.1] transition-colors"
            onClick={() => setWizardOpen(true)}
            data-testid="guided-setup-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Get started with Guided Setup</p>
                <p className="text-xs text-[hsl(215,20%,55%)]">Walk through locations, tags, notifications, and branding step by step</p>
              </div>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 flex-shrink-0" data-testid="guided-setup-banner-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Start
            </Button>
          </div>
        )}

        {/* Mobile Guided Setup Button */}
        <div className="sm:hidden">
          <Button
            onClick={() => setWizardOpen(true)}
            variant="outline"
            className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            data-testid="guided-setup-mobile-btn"
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> Guided Setup
          </Button>
        </div>

        {/* Analytics Section */}
        <Collapsible open={analyticsExpanded} onOpenChange={(open) => {
          setAnalyticsExpanded(open);
          if (open && !analyticsData) {
            fetchAnalytics();
          }
        }}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Analytics & Statistics</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      Review cards & survey performance metrics
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {analyticsExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {/* Tab Navigation */}
                <div className="flex items-center justify-between mb-4 border-b border-[hsl(217,19%,27%)] pb-3">
                  <div className="flex space-x-1">
                    <Button
                      size="sm"
                      variant={analyticsTab === 'reviews' ? 'default' : 'ghost'}
                      onClick={() => setAnalyticsTab('reviews')}
                      className={analyticsTab === 'reviews' 
                        ? 'bg-amber-600/30 text-amber-400 hover:bg-amber-600/40' 
                        : 'text-[hsl(215,20%,65%)] hover:text-amber-400'}
                    >
                      <Star className="w-4 h-4 mr-1" />
                      Review Cards
                    </Button>
                    <Button
                      size="sm"
                      variant={analyticsTab === 'surveys' ? 'default' : 'ghost'}
                      onClick={() => setAnalyticsTab('surveys')}
                      className={analyticsTab === 'surveys' 
                        ? 'bg-green-600/30 text-green-400 hover:bg-green-600/40' 
                        : 'text-[hsl(215,20%,65%)] hover:text-green-400'}
                    >
                      <ClipboardCheck className="w-4 h-4 mr-1" />
                      Room Surveys
                    </Button>
                    <Button
                      size="sm"
                      variant={analyticsTab === 'restroom' ? 'default' : 'ghost'}
                      onClick={() => setAnalyticsTab('restroom')}
                      className={analyticsTab === 'restroom' 
                        ? 'bg-blue-600/30 text-blue-400 hover:bg-blue-600/40' 
                        : 'text-[hsl(215,20%,65%)] hover:text-blue-400'}
                    >
                      <Droplets className="w-4 h-4 mr-1" />
                      Restroom Surveys
                    </Button>
                    <Button
                      size="sm"
                      variant={analyticsTab === 'retailer_alert' ? 'default' : 'ghost'}
                      onClick={() => setAnalyticsTab('retailer_alert')}
                      className={analyticsTab === 'retailer_alert' 
                        ? 'bg-fuchsia-600/30 text-fuchsia-400 hover:bg-fuchsia-600/40' 
                        : 'text-[hsl(215,20%,65%)] hover:text-fuchsia-400'}
                    >
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Retailer Alerts
                    </Button>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Select 
                      value={analyticsPeriod.toString()} 
                      onValueChange={(val) => {
                        const newPeriod = parseInt(val);
                        setAnalyticsPeriod(newPeriod);
                        if (analyticsTab === 'reviews') {
                          fetchAnalytics(newPeriod);
                        } else if (analyticsTab === 'surveys') {
                          fetchSurveyAnalytics(newPeriod, comparePeriod);
                        } else if (analyticsTab === 'restroom') {
                          fetchRestroomAnalytics(newPeriod);
                        } else if (analyticsTab === 'retailer_alert') {
                          fetchRetailerAlertAnalytics(newPeriod);
                        }
                      }}
                    >
                      <SelectTrigger className="w-28 h-8 bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                        <SelectItem value="365">1 year</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => exportAnalytics(analyticsTab)}
                      className="border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white h-8"
                      data-testid="export-csv-btn"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>

                {/* Review Cards Tab */}
                {analyticsTab === 'reviews' && (
                  <>
                    {analyticsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                        <span className="ml-3 text-[hsl(215,20%,65%)]">Loading review analytics...</span>
                      </div>
                    ) : analyticsData ? (
                      <div className="space-y-6">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                            <div className="text-2xl font-bold text-amber-400">{analyticsData.summary?.review_card_taps || 0}</div>
                            <div className="text-xs text-[hsl(215,20%,65%)]">Review Card Taps</div>
                          </div>
                          <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                            <div className="text-2xl font-bold text-green-400">{analyticsData.summary?.survey_taps || 0}</div>
                            <div className="text-xs text-[hsl(215,20%,65%)]">Survey Taps</div>
                          </div>
                          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                            <div className="text-2xl font-bold text-blue-400">{analyticsData.summary?.completed_surveys || 0}</div>
                            <div className="text-xs text-[hsl(215,20%,65%)]">Completed Surveys</div>
                          </div>
                          <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                            <div className="text-2xl font-bold text-purple-400">{analyticsData.summary?.survey_conversion_rate || 0}%</div>
                            <div className="text-xs text-[hsl(215,20%,65%)]">Survey Completion Rate</div>
                          </div>
                        </div>

                        {/* Platform Breakdown */}
                        {analyticsData.platform_breakdown && Object.keys(analyticsData.platform_breakdown).length > 0 && (
                          <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <h4 className="text-white font-medium mb-3 flex items-center">
                              <Star className="w-4 h-4 mr-2 text-amber-400" />
                              Review Platform Breakdown
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              {Object.entries(analyticsData.platform_breakdown).map(([platform, count]) => (
                                <div key={platform} className="text-center p-2 rounded bg-[hsl(217,19%,12%)]">
                                  <div className="text-lg font-bold text-white">{count}</div>
                                  <div className="text-xs text-[hsl(215,20%,65%)] capitalize">{platform}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Top Cards & Hourly Chart */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {analyticsData.card_performance?.top_review_cards?.length > 0 && (
                            <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                              <h4 className="text-white font-medium mb-3 flex items-center">
                                <Star className="w-4 h-4 mr-2 text-amber-400" />
                                Top Review Cards
                              </h4>
                              <div className="space-y-2">
                                {analyticsData.card_performance.top_review_cards.slice(0, 5).map((card, idx) => (
                                  <div key={card.card_id} className="flex items-center justify-between text-sm">
                                    <span className="text-[hsl(215,20%,65%)]">
                                      <span className="text-amber-400 font-mono mr-2">#{idx + 1}</span>
                                      {card.card_id}
                                    </span>
                                    <span className="text-white font-medium">{card.taps} taps</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {analyticsData.tap_metrics?.review_taps_by_hour && Object.keys(analyticsData.tap_metrics.review_taps_by_hour).length > 0 && (
                            <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                              <h4 className="text-white font-medium mb-3 flex items-center">
                                <Clock className="w-4 h-4 mr-2 text-purple-400" />
                                Taps by Hour
                              </h4>
                              <div className="flex items-end space-x-1 h-20">
                                {Array.from({ length: 24 }, (_, hour) => {
                                  const count = analyticsData.tap_metrics.review_taps_by_hour[hour] || 0;
                                  const maxCount = Math.max(...Object.values(analyticsData.tap_metrics.review_taps_by_hour), 1);
                                  const height = (count / maxCount) * 100;
                                  return (
                                    <div
                                      key={hour}
                                      className="flex-1 bg-purple-500/50 hover:bg-purple-500 transition-colors rounded-t"
                                      style={{ height: `${Math.max(height, 4)}%` }}
                                      title={`${hour}:00 - ${count} taps`}
                                    />
                                  );
                                })}
                              </div>
                              <div className="flex justify-between text-xs text-[hsl(215,20%,50%)] mt-1">
                                <span>12am</span>
                                <span>12pm</span>
                                <span>11pm</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {analyticsData.summary?.total_taps === 0 && (
                          <div className="text-center py-8">
                            <Activity className="w-12 h-12 text-[hsl(215,20%,35%)] mx-auto mb-3" />
                            <p className="text-[hsl(215,20%,65%)]">No review tap data yet for this period</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Activity className="w-12 h-12 text-[hsl(215,20%,35%)] mx-auto mb-3" />
                        <p className="text-[hsl(215,20%,65%)] mb-3">Load analytics data for this hotel</p>
                        <Button onClick={() => fetchAnalytics()} className="bg-amber-600 hover:bg-amber-700">
                          <TrendingUp className="w-4 h-4 mr-2" />
                          Load Review Analytics
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Survey Analytics Tab */}
                {analyticsTab === 'surveys' && (
                  <>
                    {surveyAnalyticsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-green-400" />
                        <span className="ml-3 text-[hsl(215,20%,65%)]">Loading survey analytics...</span>
                      </div>
                    ) : surveyAnalytics ? (
                      <div className="space-y-6">
                        {/* Comparison Period Selector */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                          <span className="text-sm text-[hsl(215,20%,65%)]">Compare with previous period:</span>
                          <div className="flex space-x-2">
                            {[null, 7, 30, 90].map((period) => (
                              <Button
                                key={period || 'none'}
                                size="sm"
                                variant={comparePeriod === period ? 'default' : 'ghost'}
                                onClick={() => {
                                  setComparePeriod(period);
                                  fetchSurveyAnalytics(analyticsPeriod, period);
                                }}
                                className={comparePeriod === period 
                                  ? 'bg-green-600/30 text-green-400 h-7 text-xs' 
                                  : 'text-[hsl(215,20%,65%)] hover:text-green-400 h-7 text-xs'}
                              >
                                {period ? `${period}d` : 'None'}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                            <div className="text-2xl font-bold text-green-400">{surveyAnalytics.summary?.total_surveys || 0}</div>
                            <div className="text-xs text-[hsl(215,20%,65%)]">Total Survey Responses</div>
                            {surveyAnalytics.comparison && (
                              <div className="text-xs text-[hsl(215,20%,50%)] mt-1">
                                vs {surveyAnalytics.comparison.summary?.total_surveys || 0} previous
                              </div>
                            )}
                          </div>
                          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                            <div className="text-2xl font-bold text-red-400">{surveyAnalytics.summary?.low_rating_surveys || 0}</div>
                            <div className="text-xs text-[hsl(215,20%,65%)]">Low Rating Surveys</div>
                            <div className="text-xs text-red-400 mt-1">
                              {surveyAnalytics.summary?.low_rating_percentage || 0}% of total
                            </div>
                          </div>
                          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                            <div className="text-2xl font-bold text-blue-400">
                              {surveyAnalytics.question_summary?.[0]?.average?.toFixed(1) || 'N/A'}
                            </div>
                            <div className="text-xs text-[hsl(215,20%,65%)]">Avg Check-in Rating</div>
                          </div>
                        </div>

                        {/* Question Analysis */}
                        {surveyAnalytics.question_summary?.length > 0 && (
                          <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <h4 className="text-white font-medium mb-4 flex items-center">
                              <BarChart3 className="w-4 h-4 mr-2 text-blue-400" />
                              Question Analysis
                            </h4>
                            <div className="space-y-4">
                              {surveyAnalytics.question_summary.map((q) => (
                                <div key={q.question_id} className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-white">{q.question_name}</span>
                                    <span className={`text-sm font-medium ${
                                      q.type === 'rating' 
                                        ? (q.average >= 4 ? 'text-green-400' : q.average >= 3 ? 'text-amber-400' : 'text-red-400')
                                        : 'text-blue-400'
                                    }`}>
                                      {q.type === 'rating' ? `${q.average?.toFixed(1)}/5` : `${q.response_count} responses`}
                                    </span>
                                  </div>
                                  {q.type === 'rating' && (
                                    <div className="flex space-x-1 h-4">
                                      {[1, 2, 3, 4, 5].map((star) => {
                                        const count = q.distribution?.[star] || 0;
                                        const maxCount = Math.max(...Object.values(q.distribution || {}), 1);
                                        const width = (count / maxCount) * 100;
                                        return (
                                          <div key={star} className="flex-1 flex items-center space-x-1">
                                            <span className="text-xs text-[hsl(215,20%,50%)] w-4">{star}</span>
                                            <div className="flex-1 bg-[hsl(217,19%,22%)] rounded-full h-2">
                                              <div 
                                                className={`h-2 rounded-full ${
                                                  star >= 4 ? 'bg-green-500' : star >= 3 ? 'bg-amber-500' : 'bg-red-500'
                                                }`}
                                                style={{ width: `${width}%` }}
                                              />
                                            </div>
                                            <span className="text-xs text-[hsl(215,20%,50%)] w-6">{count}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {q.type === 'yes_no' && q.distribution && (
                                    <div className="flex space-x-4 text-xs">
                                      <span className="text-green-400">Yes: {q.distribution.yes || 0}</span>
                                      <span className="text-amber-400">Neutral: {q.distribution.neutral || 0}</span>
                                      <span className="text-red-400">No: {q.distribution.no || 0}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Room Performance */}
                        {surveyAnalytics.room_performance?.length > 0 && (
                          <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <h4 className="text-white font-medium mb-4 flex items-center">
                              <DoorOpen className="w-4 h-4 mr-2 text-cyan-400" />
                              Room Performance (Lowest to Highest)
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-[hsl(215,20%,65%)] border-b border-[hsl(217,19%,27%)]">
                                    <th className="text-left py-2">Room/Card</th>
                                    <th className="text-right py-2">Surveys</th>
                                    <th className="text-right py-2">Check-in</th>
                                    <th className="text-right py-2">Cleanliness</th>
                                    <th className="text-right py-2">Return</th>
                                    <th className="text-right py-2">Overall</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {surveyAnalytics.room_performance.slice(0, 10).map((room) => (
                                    <tr key={room.card_id} className="border-b border-[hsl(217,19%,22%)]">
                                      <td className="py-2 text-white font-mono text-xs">{room.card_id}</td>
                                      <td className="py-2 text-right text-[hsl(215,20%,65%)]">{room.total_surveys}</td>
                                      <td className={`py-2 text-right ${
                                        room.question_averages?.q1 >= 4 ? 'text-green-400' : 
                                        room.question_averages?.q1 >= 3 ? 'text-amber-400' : 'text-red-400'
                                      }`}>
                                        {room.question_averages?.q1?.toFixed(1) || '-'}
                                      </td>
                                      <td className={`py-2 text-right ${
                                        room.question_averages?.q3 >= 4 ? 'text-green-400' : 
                                        room.question_averages?.q3 >= 3 ? 'text-amber-400' : 'text-red-400'
                                      }`}>
                                        {room.question_averages?.q3?.toFixed(1) || '-'}
                                      </td>
                                      <td className={`py-2 text-right ${
                                        room.question_averages?.q4 >= 4 ? 'text-green-400' : 
                                        room.question_averages?.q4 >= 3 ? 'text-amber-400' : 'text-red-400'
                                      }`}>
                                        {room.question_averages?.q4?.toFixed(1) || '-'}
                                      </td>
                                      <td className={`py-2 text-right font-medium ${
                                        room.overall_average >= 4 ? 'text-green-400' : 
                                        room.overall_average >= 3 ? 'text-amber-400' : 'text-red-400'
                                      }`}>
                                        {room.overall_average?.toFixed(1) || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {surveyAnalytics.room_performance.length > 10 && (
                              <p className="text-xs text-[hsl(215,20%,50%)] mt-2">
                                Showing 10 of {surveyAnalytics.room_performance.length} rooms. Export CSV for full data.
                              </p>
                            )}
                          </div>
                        )}

                        {/* Time-based Stats */}
                        {surveyAnalytics.time_stats?.daily && Object.keys(surveyAnalytics.time_stats.daily).length > 0 && (
                          <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <h4 className="text-white font-medium mb-3 flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-purple-400" />
                              Daily Survey Volume
                            </h4>
                            <div className="flex items-end space-x-1 h-24">
                              {Object.entries(surveyAnalytics.time_stats.daily).slice(-30).map(([date, data]) => (
                                <div
                                  key={date}
                                  className="flex-1 bg-green-500/50 hover:bg-green-500 transition-colors rounded-t cursor-pointer"
                                  style={{ 
                                    height: `${Math.max((data.count / Math.max(...Object.values(surveyAnalytics.time_stats.daily).map(d => d.count), 1)) * 100, 4)}%` 
                                  }}
                                  title={`${date}: ${data.count} surveys`}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {surveyAnalytics.summary?.total_surveys === 0 && (
                          <div className="text-center py-8">
                            <FileText className="w-12 h-12 text-[hsl(215,20%,35%)] mx-auto mb-3" />
                            <p className="text-[hsl(215,20%,65%)]">No survey responses yet for this period</p>
                            <p className="text-[hsl(215,20%,50%)] text-sm mt-1">Survey analytics will appear once guests complete surveys</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 text-[hsl(215,20%,35%)] mx-auto mb-3" />
                        <p className="text-[hsl(215,20%,65%)] mb-3">Load survey analytics for this hotel</p>
                        <Button onClick={() => fetchSurveyAnalytics()} className="bg-green-600 hover:bg-green-700">
                          <ClipboardCheck className="w-4 h-4 mr-2" />
                          Load Survey Analytics
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Restroom Surveys Tab */}
                {analyticsTab === 'restroom' && (
                  <>
                    {restroomAnalyticsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin mr-2" />
                        <span className="text-[hsl(215,20%,65%)]">Loading restroom analytics...</span>
                      </div>
                    ) : restroomAnalytics ? (
                      <div className="space-y-4">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-4 gap-3">
                          <div className="p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <p className="text-xs text-[hsl(215,20%,55%)] mb-1">Total Responses</p>
                            <p className="text-xl font-bold text-white">{restroomAnalytics.total_responses || 0}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <p className="text-xs text-[hsl(215,20%,55%)] mb-1">Overall Rating</p>
                            <p className={`text-xl font-bold ${
                              (restroomAnalytics.overall_average || 0) >= 4 ? 'text-green-400' : 
                              (restroomAnalytics.overall_average || 0) >= 3 ? 'text-amber-400' : 'text-red-400'
                            }`}>
                              {restroomAnalytics.overall_average?.toFixed(1) || '0.0'}/5
                            </p>
                          </div>
                          <div className="p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <p className="text-xs text-[hsl(215,20%,55%)] mb-1">Low Ratings</p>
                            <p className="text-xl font-bold text-red-400">{restroomAnalytics.low_rating_count || 0}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <p className="text-xs text-[hsl(215,20%,55%)] mb-1">Alerts Sent</p>
                            <p className="text-xl font-bold text-blue-400">{restroomAnalytics.alerts_sent || 0}</p>
                          </div>
                        </div>

                        {/* Question Analysis */}
                        <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                          <h4 className="text-white font-medium mb-3 flex items-center">
                            <Droplets className="w-4 h-4 mr-2 text-blue-400" />
                            Question-by-Question Analysis
                          </h4>
                          <div className="space-y-3">
                            {restroomAnalytics.question_averages && Object.entries(restroomAnalytics.question_averages).map(([question, avg], idx) => (
                              <div key={idx} className="flex items-center justify-between">
                                <span className="text-sm text-[hsl(215,20%,65%)]">{question}</span>
                                <div className="flex items-center space-x-2">
                                  <div className="w-32 h-2 bg-[hsl(217,19%,25%)] rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${
                                        avg >= 4 ? 'bg-green-500' : avg >= 3 ? 'bg-amber-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${(avg / 5) * 100}%` }}
                                    />
                                  </div>
                                  <span className={`text-sm font-medium w-12 text-right ${
                                    avg >= 4 ? 'text-green-400' : avg >= 3 ? 'text-amber-400' : 'text-red-400'
                                  }`}>
                                    {avg?.toFixed(1) || '0.0'}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {(!restroomAnalytics.question_averages || Object.keys(restroomAnalytics.question_averages).length === 0) && (
                              <p className="text-sm text-[hsl(215,20%,55%)]">No question data available yet</p>
                            )}
                          </div>
                        </div>

                        {/* Recent Responses */}
                        {restroomAnalytics.recent_responses?.length > 0 && (
                          <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <h4 className="text-white font-medium mb-3 flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-blue-400" />
                              Recent Responses
                            </h4>
                            <div className="space-y-2">
                              {restroomAnalytics.recent_responses.slice(0, 5).map((response, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-[hsl(217,19%,20%)] rounded">
                                  <div className="flex items-center space-x-3">
                                    <span className="text-xs text-[hsl(215,20%,55%)]">
                                      {new Date(response.submitted_at).toLocaleDateString()}
                                    </span>
                                    <span className="text-sm text-white">{response.location_id || response.card_id}</span>
                                  </div>
                                  <span className={`text-sm font-medium ${
                                    response.average_rating >= 4 ? 'text-green-400' : 
                                    response.average_rating >= 3 ? 'text-amber-400' : 'text-red-400'
                                  }`}>
                                    {response.average_rating?.toFixed(1) || '-'}/5
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Droplets className="w-12 h-12 text-[hsl(215,20%,35%)] mx-auto mb-3" />
                        <p className="text-[hsl(215,20%,65%)] mb-3">Load restroom survey analytics for this property</p>
                        <Button onClick={() => fetchRestroomAnalytics()} className="bg-blue-600 hover:bg-blue-700">
                          <Droplets className="w-4 h-4 mr-2" />
                          Load Restroom Analytics
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Retailer Alerts Tab */}
                {analyticsTab === 'retailer_alert' && (
                  <>
                    {retailerAlertAnalyticsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-fuchsia-500 animate-spin mr-2" />
                        <span className="text-[hsl(215,20%,65%)]">Loading retailer alert analytics...</span>
                      </div>
                    ) : retailerAlertAnalytics ? (
                      <div className="space-y-4">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <p className="text-xs text-[hsl(215,20%,55%)] mb-1">Total Reports</p>
                            <p className="text-xl font-bold text-white">{retailerAlertAnalytics.total_reports || 0}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <p className="text-xs text-[hsl(215,20%,55%)] mb-1">Alerts Sent</p>
                            <p className="text-xl font-bold text-fuchsia-400">{retailerAlertAnalytics.alerts_sent || 0}</p>
                          </div>
                        </div>

                        {/* Category Breakdown */}
                        <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                          <h4 className="text-white font-medium mb-3 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-2 text-fuchsia-400" />
                            Reports by Category
                          </h4>
                          <div className="space-y-3">
                            {retailerAlertAnalytics.category_counts && Object.entries(retailerAlertAnalytics.category_counts).map(([category, count], idx) => (
                              <div key={idx} className="flex items-center justify-between">
                                <span className="text-sm text-[hsl(215,20%,65%)]">{category}</span>
                                <div className="flex items-center space-x-2">
                                  <div className="w-32 h-2 bg-[hsl(217,19%,25%)] rounded-full overflow-hidden">
                                    <div 
                                      className="h-full rounded-full bg-fuchsia-500"
                                      style={{ width: `${Math.min((count / (retailerAlertAnalytics.total_reports || 1)) * 100, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium w-8 text-right text-fuchsia-400">{count}</span>
                                </div>
                              </div>
                            ))}
                            {(!retailerAlertAnalytics.category_counts || Object.keys(retailerAlertAnalytics.category_counts).length === 0) && (
                              <p className="text-sm text-[hsl(215,20%,55%)]">No report data available yet</p>
                            )}
                          </div>
                        </div>

                        {/* Recent Reports */}
                        {retailerAlertAnalytics.recent_reports?.length > 0 && (
                          <div className="p-4 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,27%)]">
                            <h4 className="text-white font-medium mb-3 flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-fuchsia-400" />
                              Recent Reports
                            </h4>
                            <div className="space-y-2">
                              {retailerAlertAnalytics.recent_reports.slice(0, 5).map((report, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-[hsl(217,19%,20%)] rounded">
                                  <div className="flex items-center space-x-3">
                                    <span className="text-xs text-[hsl(215,20%,55%)]">
                                      {new Date(report.submitted_at).toLocaleDateString()}
                                    </span>
                                    <span className="text-sm text-fuchsia-300 font-medium">{report.category_name}</span>
                                  </div>
                                  <span className="text-xs text-[hsl(215,20%,55%)]">
                                    {report.text_comment ? 'Has comment' : ''}
                                    {report.has_photo ? ' + Photo' : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <AlertCircle className="w-12 h-12 text-[hsl(215,20%,35%)] mx-auto mb-3" />
                        <p className="text-[hsl(215,20%,65%)] mb-3">Load retailer alert analytics for this property</p>
                        <Button onClick={() => fetchRetailerAlertAnalytics()} className="bg-fuchsia-600 hover:bg-fuchsia-700">
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Load Retailer Alert Analytics
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section 1: Room Tags */}
        <Collapsible open={roomsExpanded} onOpenChange={setRoomsExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Room Tags (Guest Surveys)</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {rooms.filter(r => r.tag_id).length} of {rooms.length} rooms have tags
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openRoomDialog(); }}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="add-room-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Room
                  </Button>
                  {roomsExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {rooms.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-[hsl(217,19%,27%)] rounded-lg">
                    <DoorOpen className="w-10 h-10 text-[hsl(215,20%,45%)] mx-auto mb-2" />
                    <p className="text-[hsl(215,20%,65%)] text-sm mb-3">No rooms yet</p>
                    <Button size="sm" onClick={() => openRoomDialog()} className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Add First Room
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {rooms.map((room) => (
                      <div
                        key={room.id}
                        className={`p-3 rounded-lg border transition-all ${
                          room.tag_id 
                            ? 'bg-green-500/5 border-green-500/30' 
                            : 'bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)]'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              room.tag_id ? 'bg-green-500/20' : 'bg-[hsl(217,19%,25%)]'
                            }`}>
                              <DoorOpen className={`w-4 h-4 ${room.tag_id ? 'text-green-400' : 'text-[hsl(215,20%,55%)]'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-white text-sm">Room {room.identifier}</p>
                              <p className="text-xs text-[hsl(215,20%,55%)]">{room.floor ? `Floor ${room.floor}` : room.room_type}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button onClick={() => openRoomDialog(room)} className="p-1 rounded hover:bg-[hsl(217,19%,25%)] text-[hsl(215,20%,55%)]">
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => deleteRoom(room.id)} className="p-1 rounded hover:bg-red-500/20 text-[hsl(215,20%,55%)] hover:text-red-400">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Tag Assignment - Inline */}
                        {assigningTagForRoom?.id === room.id ? (
                          <div className="space-y-3 p-3 bg-[hsl(217,19%,10%)] rounded-lg">
                            {/* Tag ID Input */}
                            <div className="space-y-1">
                              <Label className="text-xs text-[hsl(215,20%,65%)]">Tag ID</Label>
                              <div className="flex items-center space-x-2">
                                {hotel.hotel_code && (
                                  <span className="text-xs text-blue-400 font-mono bg-blue-500/20 px-2 py-1 rounded">
                                    {hotel.hotel_code}-
                                  </span>
                                )}
                                <Input
                                  value={tagId}
                                  onChange={(e) => setTagId(e.target.value.toUpperCase())}
                                  placeholder="Enter tag ID"
                                  className="h-8 text-sm bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                                  autoFocus
                                />
                              </div>
                            </div>
                            
                            {/* Location Label */}
                            <div className="space-y-1">
                              <Label className="text-xs text-[hsl(215,20%,65%)]">Location Label <span className="text-[hsl(215,20%,45%)]">(shows in alerts)</span></Label>
                              <Input
                                value={roomTagLabel}
                                onChange={(e) => setRoomTagLabel(e.target.value)}
                                placeholder="e.g., Suite 305, Pool Deck, Lobby Front"
                                className="h-8 text-sm bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                                data-testid="room-tag-label-input"
                              />
                            </div>

                            {/* Alert Recipient */}
                            <div className="space-y-2 p-2 rounded bg-orange-500/10 border border-orange-500/20">
                              <div className="flex items-center space-x-2">
                                <UserPlus className="w-3.5 h-3.5 text-orange-400" />
                                <span className="text-xs text-orange-400 font-medium">Low Rating Alert</span>
                              </div>
                              <p className="text-xs text-[hsl(215,20%,55%)]">
                                Notify when guest gives ≤3 stars
                              </p>
                              
                              <Select 
                                value={alertAssignmentType} 
                                onValueChange={(val) => {
                                  setAlertAssignmentType(val);
                                  if (val === 'none') {
                                    setAlertUserId('');
                                    setAlertCustomEmail('');
                                  }
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white">
                                  <SelectValue placeholder="Select notification type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No alert notifications</SelectItem>
                                  <SelectItem value="user">Select from hotel staff</SelectItem>
                                  <SelectItem value="custom">Enter custom email</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {alertAssignmentType === 'user' && (
                                <Select value={alertUserId} onValueChange={setAlertUserId}>
                                  <SelectTrigger className="h-8 text-xs bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white">
                                    <SelectValue placeholder="Select staff member" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {/* Show system users */}
                                    {users.length > 0 && (
                                      <>
                                        <div className="px-2 py-1 text-xs text-[hsl(215,20%,55%)] font-medium">System Users</div>
                                        {users.map((user) => (
                                          <SelectItem key={`user-${user.id}`} value={user.id}>
                                            {user.name || user.email} {user.role === 'super_admin' ? '(Admin)' : ''}
                                          </SelectItem>
                                        ))}
                                      </>
                                    )}
                                    {/* Show employees */}
                                    {employees.length > 0 && (
                                      <>
                                        <div className="px-2 py-1 text-xs text-[hsl(215,20%,55%)] font-medium border-t border-[hsl(217,19%,27%)] mt-1 pt-1">Staff</div>
                                        {employees.map((emp) => (
                                          <SelectItem key={`emp-${emp.id}`} value={emp.id}>
                                            {emp.name} ({emp.role || 'staff'})
                                          </SelectItem>
                                        ))}
                                      </>
                                    )}
                                    {users.length === 0 && employees.length === 0 && (
                                      <div className="px-2 py-2 text-xs text-[hsl(215,20%,55%)]">No staff available. Add employees first.</div>
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                              
                              {alertAssignmentType === 'custom' && (
                                <Input
                                  type="email"
                                  value={alertCustomEmail}
                                  onChange={(e) => setAlertCustomEmail(e.target.value)}
                                  placeholder="manager@hotel.com"
                                  className="h-8 text-xs bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                                />
                              )}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex space-x-2">
                              <Button size="sm" onClick={assignTag} disabled={saving} className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => {
                                setAssigningTagForRoom(null);
                                setAlertAssignmentType('none');
                                setAlertUserId('');
                                setAlertCustomEmail('');
                                setRoomTagLabel('');
                              }} className="h-7 text-xs">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : room.tag_id ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-2 rounded bg-green-500/10 border border-green-500/20">
                              <div className="flex items-center space-x-2">
                                <Tag className="w-3.5 h-3.5 text-green-400" />
                                <span className="text-xs text-green-400 font-mono">{room.tag_id}</span>
                                {room.tag_label && (
                                  <span className="text-xs text-green-300/70">— {room.tag_label}</span>
                                )}
                              </div>
                              <button
                                onClick={() => copyToClipboard(`${PUBLIC_URL}/standalone/room/${room.tag_id}`, `room-${room.id}`)}
                                className="p-1 rounded hover:bg-green-500/20"
                              >
                                {copiedId === `room-${room.id}` ? (
                                  <Check className="w-3 h-3 text-green-400" />
                                ) : (
                                  <Copy className="w-3 h-3 text-green-400" />
                                )}
                              </button>
                            </div>
                            {/* Show alert recipient if set */}
                            {(room.alert_user_id || room.alert_email) && (
                              <div className="flex items-center space-x-2 p-2 rounded bg-orange-500/10 border border-orange-500/20">
                                <UserPlus className="w-3 h-3 text-orange-400" />
                                <span className="text-xs text-orange-400">
                                  {room.alert_user_name || room.alert_user_email || room.alert_email || 'Alert set'}
                                </span>
                              </div>
                            )}
                            <div className="flex space-x-1">
                              <Button size="sm" variant="ghost" onClick={() => startAssignTag(room)} className="flex-1 h-7 text-xs">
                                Edit
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => unassignTag(room)} className="h-7 text-xs text-red-400 hover:text-red-300">
                                Remove
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => startAssignTag(room)}
                            className="w-full h-8 text-xs bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30"
                          >
                            <Tag className="w-3.5 h-3.5 mr-1" />
                            Assign Tag
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section 1.5: Restroom Tags (Blue scheme) */}
        <Collapsible open={restroomTagsExpanded} onOpenChange={setRestroomTagsExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Droplets className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Restroom Tags (Restroom Surveys)</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {restroomLocations.filter(r => r.tag_id).length} of {restroomLocations.length} locations have tags
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openRestroomLocationDialog(); }}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="add-restroom-location-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Location
                  </Button>
                  {restroomTagsExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {restroomLocations.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-[hsl(217,19%,27%)] rounded-lg">
                    <Droplets className="w-10 h-10 text-[hsl(215,20%,45%)] mx-auto mb-2" />
                    <p className="text-[hsl(215,20%,65%)] text-sm mb-3">No restroom locations yet</p>
                    <Button size="sm" onClick={() => openRestroomLocationDialog()} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Add First Location
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {restroomLocations.map((location) => (
                      <div
                        key={location.id}
                        className={`p-3 rounded-lg border transition-all ${
                          location.tag_id 
                            ? 'bg-blue-500/5 border-blue-500/30' 
                            : 'bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)]'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              location.tag_id ? 'bg-blue-500/20' : 'bg-[hsl(217,19%,25%)]'
                            }`}>
                              <Droplets className={`w-4 h-4 ${location.tag_id ? 'text-blue-400' : 'text-[hsl(215,20%,55%)]'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-white text-sm">{location.name || 'Restroom'}</p>
                              <p className="text-xs text-[hsl(215,20%,55%)]">{location.floor ? `Floor ${location.floor}` : location.building || 'Main'}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button onClick={() => openRestroomLocationDialog(location)} className="p-1.5 rounded hover:bg-[hsl(217,19%,25%)]">
                              <Edit className="w-3.5 h-3.5 text-[hsl(215,20%,55%)]" />
                            </button>
                            <button onClick={() => deleteRestroomLocation(location.id)} className="p-1.5 rounded hover:bg-red-500/20">
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Tag Assignment UI */}
                        {assigningTagForRestroom === location.id ? (
                          <div className="space-y-2 mt-2">
                            <Input
                              placeholder="Enter tag ID (e.g., BUCEE-REST-001)"
                              value={restroomTagId}
                              onChange={(e) => setRestroomTagId(e.target.value.toUpperCase())}
                              className="h-8 text-xs bg-[hsl(217,19%,15%)] border-blue-500/30"
                              data-testid="restroom-tag-input"
                            />
                            <Input
                              placeholder="Location label (e.g., Men's 2nd Floor, Staff Restroom)"
                              value={restroomTagLabel}
                              onChange={(e) => setRestroomTagLabel(e.target.value)}
                              className="h-8 text-xs bg-[hsl(217,19%,15%)] border-blue-500/30"
                              data-testid="restroom-tag-label-input"
                            />
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                onClick={() => assignRestroomTag(location)}
                                disabled={!restroomTagId.trim()}
                                className="flex-1 h-7 text-xs bg-blue-600 hover:bg-blue-700"
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setAssigningTagForRestroom(null); setRestroomTagId(''); setRestroomTagLabel(''); }}
                                className="h-7 text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : location.tag_id ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-2 rounded bg-blue-500/10 border border-blue-500/20">
                              <div className="flex items-center space-x-2">
                                <Tag className="w-3.5 h-3.5 text-blue-400" />
                                <span className="text-xs text-blue-400 font-mono">{location.tag_id}</span>
                                {location.tag_label && (
                                  <span className="text-xs text-blue-300/70">— {location.tag_label}</span>
                                )}
                              </div>
                              <button
                                onClick={() => copyToClipboard(`${PUBLIC_URL}/restroom-survey/${id}/${location.id}`, `restroom-${location.id}`)}
                                className="p-1 rounded hover:bg-blue-500/20"
                              >
                                {copiedId === `restroom-${location.id}` ? (
                                  <Check className="w-3 h-3 text-blue-400" />
                                ) : (
                                  <Copy className="w-3 h-3 text-blue-400" />
                                )}
                              </button>
                            </div>
                            <div className="flex space-x-1">
                              <Button size="sm" variant="ghost" onClick={() => startAssignRestroomTag(location)} className="flex-1 h-7 text-xs">
                                Edit
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => unassignRestroomTag(location)} className="h-7 text-xs text-red-400 hover:text-red-300">
                                Remove
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => startAssignRestroomTag(location)}
                            className="w-full h-8 text-xs bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/30"
                          >
                            <Tag className="w-3.5 h-3.5 mr-1" />
                            Assign Tag
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section 1.6: Retailer Alert Tags (Orange scheme) */}
        <Collapsible open={retailerAlertTagsExpanded} onOpenChange={setRetailerAlertTagsExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-fuchsia-500/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Retailer Alert Tags</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {retailerAlertLocations.filter(r => r.tag_id).length} of {retailerAlertLocations.length} locations have tags
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openRetailerAlertLocationDialog(); }}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700"
                    data-testid="add-retailer-alert-location-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Location
                  </Button>
                  {retailerAlertTagsExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {retailerAlertLocations.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-[hsl(217,19%,27%)] rounded-lg">
                    <AlertCircle className="w-10 h-10 text-[hsl(215,20%,45%)] mx-auto mb-2" />
                    <p className="text-[hsl(215,20%,65%)] text-sm mb-3">No retailer alert locations yet</p>
                    <Button size="sm" onClick={() => openRetailerAlertLocationDialog()} className="bg-fuchsia-600 hover:bg-fuchsia-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Add First Location
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {retailerAlertLocations.map((location) => (
                      <div
                        key={location.id}
                        className={`p-3 rounded-lg border transition-all ${
                          location.tag_id 
                            ? 'bg-fuchsia-500/5 border-fuchsia-500/30' 
                            : 'bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)]'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              location.tag_id ? 'bg-fuchsia-500/20' : 'bg-[hsl(217,19%,25%)]'
                            }`}>
                              <AlertCircle className={`w-4 h-4 ${location.tag_id ? 'text-fuchsia-400' : 'text-[hsl(215,20%,55%)]'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-white text-sm">{location.name || 'Store'}</p>
                              <p className="text-xs text-[hsl(215,20%,55%)]">{location.floor ? `Floor ${location.floor}` : location.building || 'Main'}{location.zone_number ? ` · Zone ${location.zone_number}` : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button onClick={() => openRetailerAlertLocationDialog(location)} className="p-1.5 rounded hover:bg-[hsl(217,19%,25%)]">
                              <Edit className="w-3.5 h-3.5 text-[hsl(215,20%,55%)]" />
                            </button>
                            <button onClick={() => deleteRetailerAlertLocation(location.id)} className="p-1.5 rounded hover:bg-red-500/20">
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Tag Assignment UI */}
                        {assigningTagForRetailerAlert === location.id ? (
                          <div className="space-y-2 mt-2">
                            <Input
                              placeholder="Enter tag ID (e.g., STORE-ALERT-001)"
                              value={retailerAlertTagId}
                              onChange={(e) => setRetailerAlertTagId(e.target.value.toUpperCase())}
                              className="h-8 text-xs bg-[hsl(217,19%,15%)] border-fuchsia-500/30"
                              data-testid="retailer-alert-tag-input"
                            />
                            <Input
                              placeholder="Location label (e.g., Aisle 20 Front, D021 Dairy)"
                              value={retailerAlertTagLabel}
                              onChange={(e) => setRetailerAlertTagLabel(e.target.value)}
                              className="h-8 text-xs bg-[hsl(217,19%,15%)] border-fuchsia-500/30"
                              data-testid="retailer-alert-tag-label-input"
                            />
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                onClick={() => assignRetailerAlertTag(location)}
                                disabled={!retailerAlertTagId.trim()}
                                className="flex-1 h-7 text-xs bg-fuchsia-600 hover:bg-fuchsia-700"
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setAssigningTagForRetailerAlert(null); setRetailerAlertTagId(''); setRetailerAlertTagLabel(''); }}
                                className="h-7 text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : location.tag_id ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-2 rounded bg-fuchsia-500/10 border border-fuchsia-500/20">
                              <div className="flex items-center space-x-2">
                                <Tag className="w-3.5 h-3.5 text-fuchsia-400" />
                                <span className="text-xs text-fuchsia-400 font-mono">{location.tag_id}</span>
                                {location.tag_label && (
                                  <span className="text-xs text-fuchsia-300/70">— {location.tag_label}</span>
                                )}
                              </div>
                              <button
                                onClick={() => copyToClipboard(`${PUBLIC_URL}/retailer-alert-survey/${id}/${location.id}`, `retailer-${location.id}`)}
                                className="p-1 rounded hover:bg-fuchsia-500/20"
                              >
                                {copiedId === `retailer-${location.id}` ? (
                                  <Check className="w-3 h-3 text-fuchsia-400" />
                                ) : (
                                  <Copy className="w-3 h-3 text-fuchsia-400" />
                                )}
                              </button>
                            </div>
                            <div className="flex space-x-1">
                              <Button size="sm" variant="ghost" onClick={() => startAssignRetailerAlertTag(location)} className="flex-1 h-7 text-xs">
                                Edit
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => unassignRetailerAlertTag(location)} className="h-7 text-xs text-red-400 hover:text-red-300">
                                Remove
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => startAssignRetailerAlertTag(location)}
                            variant="outline"
                            className="w-full h-8 text-xs border-dashed border-fuchsia-500/30 text-fuchsia-400 hover:bg-fuchsia-500/10"
                          >
                            <Tag className="w-3 h-3 mr-1" />
                            Assign Tag
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Quick Setup Card */}
        <Card className="bg-[hsl(217,19%,12%)] border-primary/40">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white font-medium">Deploy a New Card</p>
                <p className="text-xs text-[hsl(215,20%,55%)]">Guided step-by-step setup for any card type</p>
              </div>
              <Button
                onClick={() => navigate(`/standalone/setup-card?org=${id}`)}
                size="sm"
                className="bg-primary hover:bg-primary/90"
                data-testid="org-setup-card-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Setup Card
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Review Cards */}
        <Collapsible open={reviewCardsExpanded} onOpenChange={setReviewCardsExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Review Cards</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {configuredUrlCount} platform{configuredUrlCount !== 1 ? 's' : ''} configured
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {configuredUrlCount > 0 && (
                    <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                      Default: {defaultPlatform}
                    </Badge>
                  )}
                  {reviewCardsExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                {/* Platform URLs */}
                <div className="space-y-3">
                  {[
                    { key: 'google', label: 'Google Reviews', placeholder: 'https://g.page/r/your-link' },
                    { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/your-page/reviews' },
                    { key: 'tripadvisor', label: 'TripAdvisor', placeholder: 'https://tripadvisor.com/...' },
                    { key: 'yelp', label: 'Yelp', placeholder: 'https://yelp.com/writeareview/...' },
                    { key: 'trustpilot', label: 'Trustpilot', placeholder: 'https://trustpilot.com/...' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} className="flex items-center space-x-3">
                      <div className="w-28 flex items-center space-x-2">
                        <input
                          type="radio"
                          name="defaultPlatform"
                          checked={defaultPlatform === key}
                          onChange={() => setDefaultPlatform(key)}
                          className="w-4 h-4 text-amber-500"
                        />
                        <Label className="text-sm text-[hsl(215,20%,75%)]">{label}</Label>
                      </div>
                      <Input
                        value={reviewUrls[key]}
                        onChange={(e) => setReviewUrls({ ...reviewUrls, [key]: e.target.value })}
                        placeholder={placeholder}
                        className="flex-1 h-9 text-sm bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                      />
                      {reviewUrls[key] && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(reviewUrls[key], '_blank')}
                          className="h-9 px-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-[hsl(217,19%,27%)]">
                  <p className="text-xs text-[hsl(215,20%,55%)]">
                    Select default platform for NFC card redirects
                  </p>
                  <Button
                    onClick={saveEngagementSettings}
                    disabled={saving}
                    className="bg-amber-600 hover:bg-amber-700"
                    size="sm"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save URLs
                  </Button>
                </div>

                {/* Manage Cards Button */}
                <div className="p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white font-medium">Review Engine Cards</p>
                      <p className="text-xs text-[hsl(215,20%,55%)]">Create and manage NFC cards for reviews</p>
                    </div>
                    <Button
                      onClick={() => navigate('/standalone/cards?type=tap_engine')}
                      variant="outline"
                      size="sm"
                      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Manage Cards
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section 3: Employees */}
        <Collapsible open={employeesExpanded} onOpenChange={setEmployeesExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Staff & Employees</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {employees.length} employee{employees.length !== 1 ? 's' : ''} • Assign to receive alerts
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openEmployeeDialog(); }}
                    className="bg-cyan-600 hover:bg-cyan-700 h-8"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                  {employeesExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-3">
                {employees.length === 0 ? (
                  <div className="text-center py-6 text-[hsl(215,20%,55%)]">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No employees yet</p>
                    <p className="text-xs mt-1">Add staff to receive low-rating alerts</p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {employees.map((emp) => (
                      <div 
                        key={emp.id} 
                        className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg hover:bg-[hsl(217,19%,17%)] transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 rounded-full bg-cyan-500/20 flex items-center justify-center">
                            <span className="text-cyan-400 font-medium text-sm">
                              {emp.name?.charAt(0).toUpperCase() || 'E'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm text-white font-medium">{emp.name}</p>
                            <div className="flex items-center space-x-3 text-xs text-[hsl(215,20%,55%)]">
                              <span className="flex items-center">
                                <Mail className="w-3 h-3 mr-1" />
                                {emp.email}
                              </span>
                              {emp.phone && (
                                <span className="flex items-center">
                                  <Phone className="w-3 h-3 mr-1" />
                                  {emp.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              emp.role === 'admin' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                              emp.role === 'manager' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                              'bg-gray-500/20 text-gray-400 border-gray-500/30'
                            }`}
                          >
                            {emp.role || 'staff'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEmployeeDialog(emp)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteEmployee(emp.id)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-[hsl(215,20%,50%)] mt-2">
                  Staff members can be assigned to room tags to receive alerts when guests give low ratings.
                </p>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section: User-to-Property Assignment */}
        <Collapsible open={usersExpanded} onOpenChange={(open) => {
          setUsersExpanded(open);
          if (open && allUsers.length === 0) fetchAllUsers();
        }}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">User Access</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {assignedUsers.length} user{assignedUsers.length !== 1 ? 's' : ''} assigned to this property
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    data-testid="assign-user-btn"
                    onClick={(e) => { e.stopPropagation(); if (allUsers.length === 0) fetchAllUsers(); setAssignUserDialogOpen(true); }}
                    className="bg-indigo-600 hover:bg-indigo-700 h-8"
                  >
                    <UserPlus className="w-4 h-4 mr-1" /> Assign
                  </Button>
                  {usersExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-3">
                {assignedUsers.length === 0 ? (
                  <div className="text-center py-6 text-[hsl(215,20%,55%)]" data-testid="no-assigned-users">
                    <UserPlus className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No users assigned</p>
                    <p className="text-xs mt-1">Assign users to manage this property</p>
                  </div>
                ) : (
                  <div className="grid gap-2" data-testid="assigned-users-list">
                    {assignedUsers.map((au) => (
                      <div 
                        key={au.assignment_id || au.user_id} 
                        data-testid={`assigned-user-${au.user_id}`}
                        className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg hover:bg-[hsl(217,19%,17%)] transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 rounded-full bg-indigo-500/20 flex items-center justify-center">
                            <span className="text-indigo-400 font-medium text-sm">
                              {au.user_name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm text-white font-medium">{au.user_name}</p>
                            <div className="flex items-center space-x-3 text-xs text-[hsl(215,20%,55%)]">
                              <span className="flex items-center">
                                <Mail className="w-3 h-3 mr-1" />
                                {au.user_email}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge 
                            variant="outline" 
                            className="text-xs bg-indigo-500/20 text-indigo-400 border-indigo-500/30"
                          >
                            {au.role || 'admin'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`unassign-user-${au.user_id}`}
                            onClick={() => unassignUser(au.user_id)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-[hsl(215,20%,50%)] mt-2">
                  Assigned users can view and manage this property. Super admins always have access to all properties.
                </p>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>


        {/* Section 4: Settings (Collapsed by default) */}
        <Collapsible open={settingsExpanded} onOpenChange={setSettingsExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Advanced Settings</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      Survey config, notifications, and more
                    </CardDescription>
                  </div>
                </div>
                {settingsExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                {/* Alert & SMS Notifications — placed first for easy access */}
                <div className="border border-emerald-500/30 rounded-lg p-4 space-y-4 bg-emerald-500/[0.04]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Smartphone className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="text-sm text-white font-medium">Alert & SMS Notifications</p>
                        <p className="text-xs text-[hsl(215,20%,55%)]">Recipients, SMS delivery, and escalation</p>
                      </div>
                    </div>
                    {alertRecipients.length > 0 && (
                      <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">{alertRecipients.length} recipient{alertRecipients.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>

                  {/* Sub-section toggle for recipients */}
                  <Collapsible open={notificationsExpanded} onOpenChange={setNotificationsExpanded}>
                    <CollapsibleTrigger className="w-full flex items-center justify-between p-2 bg-[hsl(217,19%,13%)] rounded-lg cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors" data-testid="notif-recipients-toggle">
                      <div className="flex items-center space-x-2">
                        <Bell className="w-4 h-4 text-emerald-400" />
                        <p className="text-sm text-[hsl(215,20%,75%)]">Alert Recipients</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openRecipientDialog(); }}
                          className="bg-emerald-600 hover:bg-emerald-700 h-7 px-3 text-xs"
                          data-testid="add-recipient-btn"
                        >
                          <UserPlus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                        {notificationsExpanded ? <ChevronDown className="w-4 h-4 text-[hsl(215,20%,55%)]" /> : <ChevronRight className="w-4 h-4 text-[hsl(215,20%,55%)]" />}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 space-y-2">
                        {alertRecipients.length === 0 ? (
                          <div className="text-center py-4 text-[hsl(215,20%,45%)]">
                            <p className="text-xs">No recipients yet. Click "Add" to start.</p>
                          </div>
                        ) : (
                          alertRecipients.map((r) => (
                            <div key={r.id} className="flex items-center justify-between p-2.5 bg-[hsl(217,19%,15%)] rounded-lg group" data-testid={`recipient-${r.id}`}>
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${r.type === 'sms' ? 'bg-emerald-400' : 'bg-blue-400'}`} />
                                <div className="min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm text-white truncate">{r.name || (r.type === 'sms' && r.phone_number ? r.phone_number : r.address)}</p>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${r.type === 'sms' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                      {r.type.toUpperCase()}
                                    </span>
                                    {r.is_escalation && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">ESCALATION</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-[hsl(215,20%,45%)] truncate">
                                    {r.type === 'sms' && r.phone_number
                                      ? `${r.phone_number} - ${carrierList.find(c => c.id === r.carrier)?.name || r.carrier || 'Unknown carrier'}`
                                      : r.address
                                    }
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[hsl(215,20%,55%)] hover:text-white" onClick={() => openRecipientDialog(r)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-300" onClick={() => deleteRecipient(r.id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Alert Method selector */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-[hsl(215,20%,65%)] uppercase tracking-wider">Alert Delivery Method</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: "email", icon: Mail, label: "Email Only", color: "text-blue-400" },
                        { value: "sms", icon: Smartphone, label: "SMS Only", color: "text-emerald-400" },
                        { value: "both", icon: Bell, label: "Both", color: "text-amber-400" },
                      ].map(({ value, icon: Icon, label, color }) => (
                        <button
                          key={value}
                          onClick={() => setAlertMethod(value)}
                          data-testid={`alert-method-${value}`}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                            alertMethod === value
                              ? 'bg-[hsl(217,19%,20%)] border-emerald-500/50 ring-1 ring-emerald-500/30'
                              : 'bg-[hsl(217,19%,15%)] border-[hsl(217,19%,25%)] hover:border-[hsl(217,19%,35%)]'
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${alertMethod === value ? color : 'text-[hsl(215,20%,45%)]'}`} />
                          <span className={`text-xs font-medium ${alertMethod === value ? 'text-white' : 'text-[hsl(215,20%,55%)]'}`}>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Delivery toggles */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2.5 bg-[hsl(217,19%,15%)] rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Smartphone className="w-4 h-4 text-emerald-400" />
                        <div>
                          <p className="text-sm text-white">SMS Delivery</p>
                          <p className="text-xs text-[hsl(215,20%,45%)]">Send alerts via SMS gateway</p>
                        </div>
                      </div>
                      <Switch checked={smsEnabled} onCheckedChange={setSmsEnabled} data-testid="sms-toggle" />
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-[hsl(217,19%,15%)] rounded-lg">
                      <div className="flex items-center space-x-2">
                        <ShieldAlert className="w-4 h-4 text-amber-400" />
                        <div>
                          <p className="text-sm text-white">Escalation</p>
                          <p className="text-xs text-[hsl(215,20%,45%)]">Auto-notify backup contacts</p>
                        </div>
                      </div>
                      <Switch checked={escalationEnabled} onCheckedChange={setEscalationEnabled} data-testid="escalation-toggle" />
                    </div>
                    {escalationEnabled && (
                      <div className="p-2.5 bg-[hsl(217,19%,15%)] rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-[hsl(215,20%,55%)]" />
                          <span className="text-xs text-[hsl(215,20%,65%)]">Escalation window:</span>
                          <Input
                            type="number"
                            min={1}
                            max={60}
                            value={escalationWindow}
                            onChange={(e) => setEscalationWindow(parseInt(e.target.value) || 5)}
                            className="w-16 h-7 bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white text-sm text-center"
                            data-testid="escalation-window-input"
                          />
                          <span className="text-xs text-[hsl(215,20%,55%)]">minutes</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Resolution Tracking */}
                  <div className="space-y-2 pt-2 border-t border-[hsl(217,19%,25%)]">
                    <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Clock className="w-4 h-4 text-cyan-400" />
                        <div>
                          <p className="text-sm text-white font-medium">Alert Resolution Tracking</p>
                          <p className="text-xs text-[hsl(215,20%,55%)]">Track how quickly issues are resolved</p>
                        </div>
                      </div>
                      <Switch
                        checked={resolutionTrackingEnabled}
                        onCheckedChange={setResolutionTrackingEnabled}
                        data-testid="resolution-tracking-toggle"
                      />
                    </div>
                    {resolutionTrackingEnabled && (
                      <div className="ml-8 p-3 bg-[hsl(217,19%,13%)] rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-[hsl(215,20%,55%)]">Auto-close alerts after</p>
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number" min="1" max="1440" value={autoCloseMinutes}
                              onChange={(e) => setAutoCloseMinutes(parseInt(e.target.value) || 20)}
                              className="w-16 h-7 bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white text-sm text-center"
                              data-testid="auto-close-minutes-input"
                            />
                            <span className="text-xs text-[hsl(215,20%,55%)]">minutes</span>
                          </div>
                        </div>
                        <p className="text-[10px] text-[hsl(215,20%,45%)]">Alerts not resolved within this window will be automatically closed</p>
                      </div>
                    )}
                  </div>

                  {/* Incident Archive Email */}
                  <div className="space-y-2 pt-2 border-t border-[hsl(217,19%,25%)]">
                    <div className="p-3 bg-[hsl(217,19%,15%)] rounded-lg space-y-2">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-4 h-4 text-blue-400" />
                        <div>
                          <p className="text-sm text-white font-medium">Incident Archive Email</p>
                          <p className="text-xs text-[hsl(215,20%,45%)]">Receives a copy of every incident report with photos attached</p>
                        </div>
                      </div>
                      <Input
                        type="email"
                        placeholder="archive-incidents@company.com"
                        value={archiveEmail}
                        onChange={(e) => setArchiveEmail(e.target.value)}
                        className="h-8 text-xs bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white placeholder:text-[hsl(215,20%,35%)]"
                        data-testid="archive-email-input"
                      />
                      <p className="text-[10px] text-[hsl(215,20%,40%)]">
                        Photos are only transmitted via email and not stored in the platform. Retailers manage their own retention policy.
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={saveNotificationSettings}
                    disabled={notifSettingsSaving}
                    size="sm"
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-notif-settings-btn"
                  >
                    {notifSettingsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Notification Settings
                  </Button>

                  {/* Open Alerts Summary */}
                  {(() => {
                    const openAlerts = alertNotifications.filter(a => !a.resolution_status || a.resolution_status === 'open');
                    const highCount = openAlerts.filter(a => a.priority === 'HIGH').length;
                    const medCount = openAlerts.filter(a => a.priority === 'MEDIUM').length;
                    const lowCount = openAlerts.filter(a => a.priority === 'LOW').length;
                    if (openAlerts.length === 0) return null;
                    return (
                      <div className="p-3 rounded-lg bg-[hsl(217,19%,13%)] border border-[hsl(217,19%,22%)]" data-testid="open-alerts-summary">
                        <p className="text-xs font-medium text-[hsl(215,20%,65%)] mb-2">Open Alerts</p>
                        <div className="flex items-center gap-4">
                          {highCount > 0 && (
                            <div className="flex items-center gap-1.5" data-testid="open-alerts-high">
                              <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                              <span className="text-sm font-semibold text-red-400">{highCount}</span>
                              <span className="text-xs text-red-400/70">High</span>
                            </div>
                          )}
                          {medCount > 0 && (
                            <div className="flex items-center gap-1.5" data-testid="open-alerts-medium">
                              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                              <span className="text-sm font-semibold text-amber-400">{medCount}</span>
                              <span className="text-xs text-amber-400/70">Medium</span>
                            </div>
                          )}
                          {lowCount > 0 && (
                            <div className="flex items-center gap-1.5" data-testid="open-alerts-low">
                              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                              <span className="text-sm font-semibold text-green-400">{lowCount}</span>
                              <span className="text-xs text-green-400/70">Low</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Alert History (collapsed) */}
                  <div className="border border-[hsl(217,19%,27%)] rounded-lg overflow-hidden">
                    <button
                      onClick={() => { setAlertHistoryExpanded(!alertHistoryExpanded); if (!alertHistoryExpanded) fetchAlertHistory(); }}
                      className="w-full flex items-center justify-between p-2.5 bg-[hsl(217,19%,15%)] cursor-pointer hover:bg-[hsl(217,19%,17%)] transition-colors"
                      data-testid="alert-history-toggle"
                    >
                      <div className="flex items-center space-x-2">
                        <ShieldAlert className="w-4 h-4 text-[hsl(215,20%,55%)]" />
                        <p className="text-xs text-[hsl(215,20%,65%)]">Alert History</p>
                      </div>
                      {alertHistoryExpanded ? <ChevronDown className="w-4 h-4 text-[hsl(215,20%,55%)]" /> : <ChevronRight className="w-4 h-4 text-[hsl(215,20%,55%)]" />}
                    </button>
                    {alertHistoryExpanded && (
                      <div className="p-3 space-y-2">
                        {/* Status filter tabs */}
                        <div className="flex gap-1.5 mb-2">
                          {[
                            { value: "all", label: "All" },
                            { value: "open", label: "Open" },
                            { value: "resolved", label: "Resolved" },
                            { value: "auto_closed", label: "Auto-Closed" },
                          ].map(({ value, label }) => (
                            <button
                              key={value}
                              onClick={() => { setAlertStatusFilter(value); fetchAlertHistory(value); }}
                              data-testid={`alert-filter-${value}`}
                              className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-all ${
                                alertStatusFilter === value
                                  ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30'
                                  : 'bg-[hsl(217,19%,20%)] text-[hsl(215,20%,50%)] hover:text-[hsl(215,20%,65%)]'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <div className="max-h-[400px] overflow-y-auto space-y-2">
                        {alertNotifications.length === 0 ? (
                          <p className="text-center py-4 text-sm text-[hsl(215,20%,45%)]">No alerts found</p>
                        ) : (
                          alertNotifications.map((alert) => {
                            const formatResponseTime = (secs) => {
                              if (!secs) return '';
                              const s = Math.floor(secs);
                              if (s < 60) return `${s}s`;
                              const m = Math.floor(s / 60);
                              const rs = s % 60;
                              if (m < 60) return `${m}m ${rs}s`;
                              const h = Math.floor(m / 60);
                              const rm = m % 60;
                              return `${h}h ${rm}m`;
                            };
                            const statusConfig = {
                              open: { label: 'OPEN', cls: 'bg-yellow-500/20 text-yellow-400' },
                              resolved: { label: 'RESOLVED', cls: 'bg-green-500/20 text-green-400' },
                              auto_closed: { label: 'AUTO-CLOSED', cls: 'bg-blue-500/20 text-blue-400' },
                            };
                            const resStatus = alert.resolution_status;
                            const statusCfg = statusConfig[resStatus] || null;
                            return (
                            <div key={alert.id} className="p-2.5 bg-[hsl(217,19%,15%)] rounded-lg" data-testid={`alert-${alert.id}`}>
                              <div className="flex items-center justify-between">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center space-x-2">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                      alert.priority === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                                      alert.priority === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                                      'bg-slate-500/20 text-slate-400'
                                    }`}>{alert.priority}</span>
                                    <p className="text-sm text-white truncate">{alert.category_name || alert.alert_type}</p>
                                  </div>
                                  <p className="text-xs text-[hsl(215,20%,45%)] mt-0.5">{alert.location_name} - {new Date(alert.created_at).toLocaleString()}</p>
                                </div>
                                <div className="flex items-center space-x-1.5 flex-shrink-0 ml-2">
                                  {alert.acknowledged ? (
                                    <span className="text-[10px] px-2 py-1 rounded bg-green-500/20 text-green-400 font-medium">ACK</span>
                                  ) : alert.escalated ? (
                                    <span className="text-[10px] px-2 py-1 rounded bg-red-500/20 text-red-400 font-medium">ESCALATED</span>
                                  ) : (
                                    <span className="text-[10px] px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 font-medium">PENDING</span>
                                  )}
                                  {statusCfg && (
                                    <span className={`text-[10px] px-2 py-1 rounded font-medium ${statusCfg.cls}`}>{statusCfg.label}</span>
                                  )}
                                </div>
                              </div>
                              {/* Resolution details */}
                              {alert.resolution_tracking_enabled && (
                                <div className="mt-1.5 pt-1.5 border-t border-[hsl(217,19%,22%)] flex items-center gap-3">
                                  {alert.resolved_at && (
                                    <p className="text-[10px] text-[hsl(215,20%,50%)]">
                                      Resolved: {new Date(alert.resolved_at).toLocaleString()}
                                    </p>
                                  )}
                                  {alert.response_time_seconds != null && (
                                    <p className="text-[10px] font-medium text-cyan-400">
                                      Response Time: {formatResponseTime(alert.response_time_seconds)}
                                    </p>
                                  )}
                                  {alert.resolved_by && (
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                                      alert.resolved_by === 'manager_link' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-500/10 text-slate-400'
                                    }`}>{alert.resolved_by === 'manager_link' ? 'Manager' : 'Auto'}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          );})
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Feature Toggles */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <ClipboardCheck className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-sm text-white font-medium">Guest Surveys</p>
                        <p className="text-xs text-[hsl(215,20%,55%)]">Enable in-room survey collection</p>
                      </div>
                    </div>
                    <Switch
                      checked={surveyEnabled}
                      onCheckedChange={setSurveyEnabled}
                      data-testid="guest-survey-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Droplets className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-sm text-white font-medium">Restroom Surveys</p>
                        <p className="text-xs text-[hsl(215,20%,55%)]">Enable restroom facility feedback</p>
                      </div>
                    </div>
                    <Switch
                      checked={restroomSurveyEnabled}
                      onCheckedChange={setRestroomSurveyEnabled}
                      data-testid="restroom-survey-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-fuchsia-400" />
                      <div>
                        <p className="text-sm text-white font-medium">Retailer Alert</p>
                        <p className="text-xs text-[hsl(215,20%,55%)]">Enable in-store reporting alerts</p>
                      </div>
                    </div>
                    <Switch
                      checked={retailerAlertEnabled}
                      onCheckedChange={setRetailerAlertEnabled}
                      data-testid="retailer-alert-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Star className="w-5 h-5 text-amber-400" />
                      <div>
                        <p className="text-sm text-white font-medium">Review Collection</p>
                        <p className="text-xs text-[hsl(215,20%,55%)]">Enable external review redirects</p>
                      </div>
                    </div>
                    <Switch
                      checked={reviewsEnabled}
                      onCheckedChange={setReviewsEnabled}
                      data-testid="review-collection-toggle"
                    />
                  </div>
                </div>

                {/* Survey Configuration Links */}
                <div className="space-y-2">
                  <Button
                    onClick={() => navigate(`/hotels/${id}/survey-config`)}
                    variant="outline"
                    className="w-full border-[hsl(217,19%,27%)] text-[hsl(215,20%,75%)] hover:bg-[hsl(217,19%,17%)]"
                    data-testid="configure-room-survey-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configure Room Survey Questions
                  </Button>
                  
                  <Button
                    onClick={() => navigate(`/hotels/${id}/restroom-survey-config`)}
                    variant="outline"
                    className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    data-testid="configure-restroom-survey-btn"
                  >
                    <Droplets className="w-4 h-4 mr-2" />
                    Configure Restroom Survey Questions
                  </Button>

                  <Button
                    onClick={() => navigate(`/hotels/${id}/retailer-alert-config`)}
                    variant="outline"
                    className="w-full border-fuchsia-500/30 text-fuchsia-400 hover:bg-fuchsia-500/10"
                    data-testid="configure-retailer-alert-btn"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Configure Retailer Alert Categories
                  </Button>
                </div>

                {/* Pyramid 2.1: Employee Codes & Override Codes */}
                <EmployeeCodesManagement orgId={id} token={localStorage.getItem('token')} />

                {/* Language Settings */}
                <div className="border border-[hsl(217,19%,27%)] rounded-lg p-4 space-y-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <Globe className="w-5 h-5 text-violet-400" />
                    <p className="text-sm text-white font-medium">Language Settings</p>
                  </div>
                  
                  {/* Room Survey Language */}
                  <div className="space-y-2 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Room Survey Default Language</p>
                      <div className="flex items-center space-x-1 bg-[hsl(217,19%,20%)] rounded-md p-0.5">
                        <button
                          onClick={() => setRoomDefaultLang('en')}
                          className={`px-3 py-1 text-xs rounded transition-colors ${roomDefaultLang === 'en' ? 'bg-green-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                          data-testid="room-lang-en"
                        >EN</button>
                        <button
                          onClick={() => setRoomDefaultLang('fr')}
                          className={`px-3 py-1 text-xs rounded transition-colors ${roomDefaultLang === 'fr' ? 'bg-blue-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                          data-testid="room-lang-fr"
                        >FR</button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[hsl(215,20%,55%)]">Show language selection landing page</p>
                      <Switch
                        checked={roomLandingPageEnabled}
                        onCheckedChange={setRoomLandingPageEnabled}
                        data-testid="room-landing-toggle"
                      />
                    </div>
                  </div>

                  {/* Restroom Survey Language */}
                  <div className="space-y-2 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Restroom Survey Default Language</p>
                      <div className="flex items-center space-x-1 bg-[hsl(217,19%,20%)] rounded-md p-0.5">
                        <button
                          onClick={() => setRestroomDefaultLang('en')}
                          className={`px-3 py-1 text-xs rounded transition-colors ${restroomDefaultLang === 'en' ? 'bg-green-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                          data-testid="restroom-lang-en"
                        >EN</button>
                        <button
                          onClick={() => setRestroomDefaultLang('fr')}
                          className={`px-3 py-1 text-xs rounded transition-colors ${restroomDefaultLang === 'fr' ? 'bg-blue-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                          data-testid="restroom-lang-fr"
                        >FR</button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[hsl(215,20%,55%)]">Show language selection landing page</p>
                      <Switch
                        checked={restroomLandingPageEnabled}
                        onCheckedChange={setRestroomLandingPageEnabled}
                        data-testid="restroom-landing-toggle"
                      />
                    </div>
                  </div>

                  {/* Retailer Alert Language */}
                  <div className="space-y-3 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Retailer Alert Default Language</p>
                      <div className="flex items-center space-x-1 bg-[hsl(217,19%,20%)] rounded-md p-0.5">
                        <button
                          onClick={() => setRetailerAlertDefaultLang('en')}
                          className={`px-3 py-1 text-xs rounded transition-colors ${retailerAlertDefaultLang === 'en' ? 'bg-fuchsia-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                          data-testid="retailer-alert-lang-en"
                        >EN</button>
                        <button
                          onClick={() => setRetailerAlertDefaultLang('fr')}
                          className={`px-3 py-1 text-xs rounded transition-colors ${retailerAlertDefaultLang === 'fr' ? 'bg-fuchsia-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                          data-testid="retailer-alert-lang-fr"
                        >FR</button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[hsl(215,20%,55%)]">Show language selection landing page</p>
                      <Switch
                        checked={retailerAlertLandingPageEnabled}
                        onCheckedChange={setRetailerAlertLandingPageEnabled}
                        data-testid="retailer-alert-landing-toggle"
                      />
                    </div>
                  </div>

                  {/* Landing Page Customization (shown if any landing page is enabled) */}
                  {(roomLandingPageEnabled || restroomLandingPageEnabled || retailerAlertLandingPageEnabled) && (
                    <div className="space-y-3 p-3 bg-[hsl(217,19%,15%)] rounded-lg border border-violet-500/20">
                      <p className="text-xs text-violet-400 font-medium">Landing Page Customization</p>
                      <div className="space-y-2">
                        <Label className="text-xs text-[hsl(215,20%,65%)]">Welcome Message</Label>
                        <input
                          type="text"
                          value={landingWelcomeMessage}
                          onChange={(e) => setLandingWelcomeMessage(e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-[hsl(217,19%,20%)] border border-[hsl(217,19%,30%)] rounded text-white"
                          data-testid="landing-welcome-message"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-[hsl(215,20%,55%)]">Show property name on landing page</p>
                        <Switch
                          checked={landingShowPropertyName}
                          onCheckedChange={setLandingShowPropertyName}
                          data-testid="landing-show-name-toggle"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Review Redirect Settings */}
                <div className="border border-[hsl(217,19%,27%)] rounded-lg p-4 space-y-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <ExternalLink className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-sm text-white font-medium">Review Redirect</p>
                      <p className="text-xs text-[hsl(215,20%,55%)]">Prompt satisfied guests to leave a public review</p>
                    </div>
                  </div>

                  {/* Room Survey Review Redirect */}
                  <div className="space-y-3 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Room Survey</p>
                        <p className="text-xs text-[hsl(215,20%,45%)]">Redirect after all 4+ star ratings</p>
                      </div>
                      <Switch
                        checked={roomReviewRedirectEnabled}
                        onCheckedChange={setRoomReviewRedirectEnabled}
                        data-testid="room-review-redirect-toggle"
                      />
                    </div>
                    {roomReviewRedirectEnabled && (
                      <Input
                        placeholder="https://g.page/r/your-google-review-link"
                        value={roomReviewRedirectUrl}
                        onChange={(e) => setRoomReviewRedirectUrl(e.target.value)}
                        className="bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white text-sm"
                        data-testid="room-review-redirect-url"
                      />
                    )}
                  </div>

                  {/* Restroom Survey Review Redirect */}
                  <div className="space-y-3 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Restroom Survey</p>
                        <p className="text-xs text-[hsl(215,20%,45%)]">Redirect after all 4+ star ratings</p>
                      </div>
                      <Switch
                        checked={restroomReviewRedirectEnabled}
                        onCheckedChange={setRestroomReviewRedirectEnabled}
                        data-testid="restroom-review-redirect-toggle"
                      />
                    </div>
                    {restroomReviewRedirectEnabled && (
                      <Input
                        placeholder="https://g.page/r/your-google-review-link"
                        value={restroomReviewRedirectUrl}
                        onChange={(e) => setRestroomReviewRedirectUrl(e.target.value)}
                        className="bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white text-sm"
                        data-testid="restroom-review-redirect-url"
                      />
                    )}
                  </div>

                  {/* Retailer Alert Review Redirect */}
                  <div className="space-y-3 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Retailer Alert</p>
                        <p className="text-xs text-[hsl(215,20%,45%)]">Redirect after report submission</p>
                      </div>
                      <Switch
                        checked={retailerAlertReviewRedirectEnabled}
                        onCheckedChange={setRetailerAlertReviewRedirectEnabled}
                        data-testid="retailer-alert-review-redirect-toggle"
                      />
                    </div>
                    {retailerAlertReviewRedirectEnabled && (
                      <Input
                        placeholder="https://g.page/r/your-google-review-link"
                        value={retailerAlertReviewRedirectUrl}
                        onChange={(e) => setRetailerAlertReviewRedirectUrl(e.target.value)}
                        className="bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white text-sm"
                        data-testid="retailer-alert-review-redirect-url"
                      />
                    )}
                  </div>
                </div>

                {/* Summary Email Settings */}
                <div className="border border-[hsl(217,19%,27%)] rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CalendarClock className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-sm text-white font-medium">Summary Email Digest</p>
                        <p className="text-xs text-[hsl(215,20%,55%)]">Receive scheduled activity summaries via email</p>
                      </div>
                    </div>
                    <Switch
                      checked={summaryEmailEnabled}
                      onCheckedChange={setSummaryEmailEnabled}
                      data-testid="summary-email-toggle"
                    />
                  </div>

                  {summaryEmailEnabled && (
                    <div className="space-y-3">
                      {/* Frequency */}
                      <div className="space-y-2 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                        <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Frequency</p>
                        <div className="flex items-center space-x-1 bg-[hsl(217,19%,20%)] rounded-md p-0.5 w-fit">
                          <button
                            onClick={() => setSummaryEmailFrequency('daily')}
                            className={`px-4 py-1.5 text-xs rounded transition-colors ${summaryEmailFrequency === 'daily' ? 'bg-cyan-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                            data-testid="summary-freq-daily"
                          >Daily</button>
                          <button
                            onClick={() => setSummaryEmailFrequency('weekly')}
                            className={`px-4 py-1.5 text-xs rounded transition-colors ${summaryEmailFrequency === 'weekly' ? 'bg-cyan-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                            data-testid="summary-freq-weekly"
                          >Weekly</button>
                        </div>
                      </div>

                      {/* Recipients */}
                      <div className="space-y-2 p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                        <p className="text-xs text-[hsl(215,20%,65%)] font-medium">Recipients</p>
                        <Input
                          placeholder="email1@example.com, email2@example.com"
                          value={summaryEmailRecipients}
                          onChange={(e) => setSummaryEmailRecipients(e.target.value)}
                          className="bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white text-sm"
                          data-testid="summary-email-recipients"
                        />
                        <p className="text-xs text-[hsl(215,20%,45%)]">Comma-separated email addresses</p>
                      </div>

                      {/* Last Sent Info */}
                      {summaryEmailLastSent && (
                        <p className="text-xs text-[hsl(215,20%,55%)] px-1">
                          Last sent: {new Date(summaryEmailLastSent).toLocaleString()}
                        </p>
                      )}

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <Button
                          onClick={saveSummaryEmailSettings}
                          disabled={summaryEmailSaving}
                          className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                          data-testid="save-summary-email-btn"
                        >
                          {summaryEmailSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Settings
                        </Button>
                        <Button
                          onClick={sendTestSummaryEmail}
                          disabled={summaryEmailSending || !summaryEmailRecipients.trim()}
                          variant="outline"
                          className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                          data-testid="send-summary-email-btn"
                        >
                          {summaryEmailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Send Now
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Survey Branding Section */}
                <Collapsible open={brandingExpanded} onOpenChange={setBrandingExpanded}>
                  <div className="border border-[hsl(217,19%,27%)] rounded-lg overflow-hidden">
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] cursor-pointer hover:bg-[hsl(217,19%,17%)] transition-colors">
                        <div className="flex items-center space-x-3">
                          <Image className="w-5 h-5 text-pink-400" />
                          <div className="text-left">
                            <p className="text-sm text-white font-medium">Survey Branding</p>
                            <p className="text-xs text-[hsl(215,20%,55%)]">Add logo to surveys</p>
                          </div>
                        </div>
                        {brandingExpanded ? <ChevronDown className="w-4 h-4 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-4 h-4 text-[hsl(215,20%,65%)]" />}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-3 space-y-4 bg-[hsl(217,19%,10%)]">
                        {/* Logo Preview */}
                        {propertyLogoUrl && (
                          <div className="relative inline-block">
                            <img 
                              src={propertyLogoUrl.startsWith('http') ? propertyLogoUrl : `${API_BASE}${propertyLogoUrl}`}
                              alt="Survey logo"
                              className={`object-contain bg-white rounded-lg p-2 ${
                                propertyLogoSize === 'small' ? 'h-12 max-w-[100px]' :
                                propertyLogoSize === 'large' ? 'h-28 max-w-[220px]' :
                                propertyLogoSize === 'very_large' ? 'h-36 max-w-[280px]' :
                                'h-20 max-w-[160px]'
                              }`}
                            />
                            <button
                              onClick={removeLogo}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                              data-testid="remove-logo-btn"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {/* Upload Options */}
                        <div className="space-y-3">
                          {/* File Upload */}
                          <div>
                            <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-[hsl(217,19%,30%)] rounded-lg cursor-pointer hover:border-pink-400/50 transition-colors">
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                                onChange={handleLogoUpload}
                                className="hidden"
                                data-testid="logo-upload-input"
                              />
                              {uploadingLogo ? (
                                <Loader2 className="w-5 h-5 animate-spin text-pink-400 mr-2" />
                              ) : (
                                <Upload className="w-5 h-5 text-pink-400 mr-2" />
                              )}
                              <span className="text-sm text-[hsl(215,20%,65%)]">
                                {uploadingLogo ? 'Uploading...' : 'Upload Image'}
                              </span>
                            </label>
                            <p className="text-xs text-[hsl(215,20%,45%)] mt-1 text-center">
                              PNG, JPEG, GIF, WebP, SVG • Max 5MB
                            </p>
                          </div>

                          {/* URL Paste */}
                          <div className="flex gap-2">
                            <Input
                              placeholder="Or paste image URL..."
                              value={logoUrlInput}
                              onChange={(e) => setLogoUrlInput(e.target.value)}
                              className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                              data-testid="logo-url-input"
                            />
                            <Button
                              onClick={handleLogoUrlPaste}
                              disabled={!logoUrlInput.trim()}
                              variant="outline"
                              className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
                              data-testid="paste-logo-url-btn"
                            >
                              Set
                            </Button>
                          </div>
                        </div>

                        {/* Size Selector */}
                        <div className="space-y-2">
                          <Label className="text-sm text-[hsl(215,20%,75%)]">Logo Size</Label>
                          <div className="grid grid-cols-4 gap-2">
                            {['small', 'medium', 'large', 'very_large'].map((size) => (
                              <button
                                key={size}
                                onClick={() => setPropertyLogoSize(size)}
                                className={`p-2 text-xs rounded-lg border transition-colors ${
                                  propertyLogoSize === size
                                    ? 'border-pink-500 bg-pink-500/20 text-pink-300'
                                    : 'border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:border-pink-500/50'
                                }`}
                                data-testid={`logo-size-${size}`}
                              >
                                {size === 'very_large' ? 'XL' : size.charAt(0).toUpperCase() + size.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Apply to all toggle */}
                        <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg">
                          <div>
                            <p className="text-sm text-white font-medium">Lock Logo for All Cards</p>
                            <p className="text-xs text-[hsl(215,20%,55%)]">Prevent individual cards from using different logos</p>
                          </div>
                          <Switch
                            checked={usePropertyLogoForAll}
                            onCheckedChange={setUsePropertyLogoForAll}
                            data-testid="apply-logo-to-all-toggle"
                          />
                        </div>

                        {/* Save Branding Button */}
                        <Button
                          onClick={saveBrandingSettings}
                          disabled={saving}
                          className="w-full bg-pink-600 hover:bg-pink-700"
                          data-testid="save-branding-btn"
                        >
                          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Branding
                        </Button>

                        {/* Preview Survey Buttons - always visible in branding section */}
                        <div className="grid grid-cols-3 gap-2 mt-3" data-testid="survey-preview-buttons">
                          <Button
                            variant="outline"
                            onClick={() => {
                              const cards = rooms.filter(r => r.tag_id);
                              const firstCard = cards.length > 0 ? cards[0] : null;
                              const loc = firstCard ? firstCard.id : 'preview';
                              window.open(`${window.location.origin}/standalone/survey/${id}/${loc}?lang=en${firstCard ? `&card=${firstCard.tag_id}` : ''}`, '_blank');
                            }}
                            className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                            data-testid="preview-room-survey-btn"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Preview Room Survey
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const firstRestroom = restroomLocations.length > 0 ? restroomLocations[0] : null;
                              if (firstRestroom) {
                                window.open(`${window.location.origin}/standalone/restroom-survey/${id}/${firstRestroom.id}?lang=en`, '_blank');
                              } else {
                                toast.error('No restroom locations configured');
                              }
                            }}
                            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                            data-testid="preview-restroom-survey-btn"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Preview Restroom Survey
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const firstRetailer = retailerAlertLocations.length > 0 ? retailerAlertLocations[0] : null;
                              if (firstRetailer) {
                                window.open(`${window.location.origin}/standalone/retailer-alert-survey/${id}/${firstRetailer.id}?lang=en`, '_blank');
                              } else {
                                toast.error('No retailer alert locations configured');
                              }
                            }}
                            className="border-fuchsia-500/30 text-fuchsia-400 hover:bg-fuchsia-500/10"
                            data-testid="preview-retailer-alert-btn"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Preview Retailer Alert
                          </Button>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                <Button
                  onClick={saveEngagementSettings}
                  disabled={saving}
                  className="w-full"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section 4: Communications & Marketing Engine (Pyramid 3) */}
        <Collapsible open={commsExpanded} onOpenChange={setCommsExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                    <Megaphone className="w-5 h-5 text-teal-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Communications & Marketing Engine</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {commsConfig.engine_enabled ? 'Enabled — Tag messaging active' : 'Disabled'}
                    </CardDescription>
                  </div>
                </div>
                {commsExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                {/* Master toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(217,19%,15%)]">
                  <div>
                    <p className="text-white font-medium text-sm">Enable Communications Engine</p>
                    <p className="text-xs text-[hsl(215,20%,50%)]">Allow messages on scanned tags</p>
                  </div>
                  <Switch checked={commsConfig.engine_enabled} onCheckedChange={v => setCommsConfig({ ...commsConfig, engine_enabled: v })} data-testid="comms-engine-toggle" />
                </div>

                {commsConfig.engine_enabled && (
                  <>
                    {/* Feature toggles */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'allow_scheduling', label: 'Allow Scheduling', desc: 'Start/end dates for messages' },
                        { key: 'allow_images', label: 'Allow Images', desc: 'Logo or custom images' },
                        { key: 'allow_price_field', label: 'Allow Price Field', desc: 'Show pricing on messages' },
                        { key: 'allow_link_url', label: 'Allow Link / URL', desc: 'External links in messages' },
                        { key: 'require_expiration', label: 'Require Expiration', desc: 'Force end date on messages' },
                      ].map(({ key, label, desc }) => (
                        <div key={key} className="flex items-center justify-between p-2.5 rounded-lg bg-[hsl(217,19%,15%)]">
                          <div>
                            <p className="text-white text-xs font-medium">{label}</p>
                            <p className="text-[10px] text-[hsl(215,20%,45%)]">{desc}</p>
                          </div>
                          <Switch checked={commsConfig[key]} onCheckedChange={v => setCommsConfig({ ...commsConfig, [key]: v })} />
                        </div>
                      ))}
                    </div>

                    {/* Default scope + Max active */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[hsl(215,20%,75%)] text-sm">Default Message Scope</Label>
                        <Select value={commsConfig.default_message_scope} onValueChange={v => setCommsConfig({ ...commsConfig, default_message_scope: v })}>
                          <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tag">Tag</SelectItem>
                            <SelectItem value="group">Group</SelectItem>
                            <SelectItem value="global">Global</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[hsl(215,20%,75%)] text-sm">Max Active Messages</Label>
                        <Input type="number" min={1} max={100} value={commsConfig.max_active_messages}
                          onChange={e => setCommsConfig({ ...commsConfig, max_active_messages: parseInt(e.target.value) || 10 })}
                          className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" />
                      </div>
                    </div>

                    {/* Link to Messages page */}
                    <Button variant="outline" onClick={() => navigate(`/hotels/${id}/communications`)}
                      className="w-full border-teal-500/30 text-teal-400 hover:bg-teal-500/10" data-testid="manage-messages-btn">
                      <Megaphone className="w-4 h-4 mr-2" /> Manage Messages
                    </Button>
                  </>
                )}

                <Button onClick={async () => {
                  setSaving(true);
                  try {
                    await fetch(`${API_BASE}/api/communications/config/${id}`, {
                      method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify(commsConfig),
                    });
                    toast.success('Communications settings saved');
                  } catch { toast.error('Failed to save'); }
                  finally { setSaving(false); }
                }} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Communications Settings
                </Button>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section 5: Property Info (Collapsed by default) */}
        <Collapsible open={infoExpanded} onOpenChange={setInfoExpanded}>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-[hsl(217,19%,15%)] transition-colors rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-white text-base">Property Info</CardTitle>
                    <CardDescription className="text-[hsl(215,20%,65%)] text-sm">
                      {hotel.city || hotel.address || 'Update property details'}
                    </CardDescription>
                  </div>
                </div>
                {infoExpanded ? <ChevronDown className="w-5 h-5 text-[hsl(215,20%,65%)]" /> : <ChevronRight className="w-5 h-5 text-[hsl(215,20%,65%)]" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-[hsl(215,20%,75%)]">Property Name</Label>
                    <Input
                      value={hotelForm.name}
                      onChange={(e) => setHotelForm({ ...hotelForm, name: e.target.value })}
                      className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[hsl(215,20%,75%)]">{brandConfig.terms.businessCode} (for tags)</Label>
                    <Input
                      value={hotelForm.hotel_code}
                      onChange={(e) => setHotelForm({ ...hotelForm, hotel_code: e.target.value.toUpperCase() })}
                      placeholder="e.g., HTLMTL"
                      className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[hsl(215,20%,75%)]">Address</Label>
                    <Input
                      value={hotelForm.address}
                      onChange={(e) => setHotelForm({ ...hotelForm, address: e.target.value })}
                      className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[hsl(215,20%,75%)]">City</Label>
                    <Input
                      value={hotelForm.city}
                      onChange={(e) => setHotelForm({ ...hotelForm, city: e.target.value })}
                      className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[hsl(215,20%,75%)]">Contact Email</Label>
                    <Input
                      value={hotelForm.contact_email}
                      onChange={(e) => setHotelForm({ ...hotelForm, contact_email: e.target.value })}
                      type="email"
                      className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[hsl(215,20%,75%)]">Contact Phone</Label>
                    <Input
                      value={hotelForm.contact_phone}
                      onChange={(e) => setHotelForm({ ...hotelForm, contact_phone: e.target.value })}
                      className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                    />
                  </div>
                </div>

                {/* Main Logo Section */}
                <div className="mt-6 p-4 rounded-lg border border-[hsl(217,19%,27%)] bg-[hsl(217,19%,10%)]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center">
                        <Image className="w-5 h-5 text-pink-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">Company / Property Logo</h3>
                        <p className="text-xs text-[hsl(215,20%,55%)]">Appears on all survey cards when enabled</p>
                      </div>
                    </div>
                    {/* Main Logo Toggle */}
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs ${mainLogoEnabled ? 'text-green-400' : 'text-[hsl(215,20%,55%)]'}`}>
                        {mainLogoEnabled ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={mainLogoEnabled}
                        onCheckedChange={setMainLogoEnabled}
                        data-testid="main-logo-toggle"
                      />
                    </div>
                  </div>

                  {/* Logo Preview */}
                  {mainLogoUrl && (
                    <div className="mb-4 p-3 bg-white rounded-lg inline-block relative">
                      <img 
                        src={mainLogoUrl.startsWith('http') ? mainLogoUrl : `${API_BASE}${mainLogoUrl}`}
                        alt="Main Logo"
                        className={`object-contain ${
                          mainLogoSize === 'small' ? 'h-12 max-w-[100px]' :
                          mainLogoSize === 'large' ? 'h-28 max-w-[220px]' :
                          mainLogoSize === 'very_large' ? 'h-36 max-w-[280px]' :
                          'h-20 max-w-[160px]'
                        }`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        onClick={() => setMainLogoUrl('')}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                        data-testid="remove-main-logo-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Upload Options */}
                  <div className="space-y-3">
                    <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-[hsl(217,19%,30%)] rounded-lg cursor-pointer hover:border-pink-400/50 transition-colors">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        onChange={handleMainLogoUpload}
                        className="hidden"
                        data-testid="main-logo-upload-input"
                      />
                      {uploadingMainLogo ? (
                        <Loader2 className="w-5 h-5 animate-spin text-pink-400 mr-2" />
                      ) : (
                        <Upload className="w-5 h-5 text-pink-400 mr-2" />
                      )}
                      <span className="text-sm text-[hsl(215,20%,65%)]">
                        {uploadingMainLogo ? 'Uploading...' : 'Upload Logo'}
                      </span>
                    </label>
                    <p className="text-xs text-[hsl(215,20%,45%)] text-center">
                      PNG, JPEG, GIF, WebP, SVG • Max 5MB
                    </p>

                    {/* URL Paste */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Or paste image URL..."
                        value={mainLogoUrlInput}
                        onChange={(e) => setMainLogoUrlInput(e.target.value)}
                        className="bg-[hsl(217,19%,15%)] border-[hsl(217,19%,27%)] text-white"
                        data-testid="main-logo-url-input"
                      />
                      <Button
                        onClick={handleMainLogoUrlPaste}
                        disabled={!mainLogoUrlInput.trim()}
                        variant="outline"
                        className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
                        data-testid="paste-main-logo-url-btn"
                      >
                        Set
                      </Button>
                    </div>
                  </div>

                  {/* Size Selector */}
                  {mainLogoUrl && (
                    <div className="mt-4 space-y-2">
                      <Label className="text-sm text-[hsl(215,20%,75%)]">Logo Size</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {['small', 'medium', 'large', 'very_large'].map((size) => (
                          <button
                            key={size}
                            onClick={() => setMainLogoSize(size)}
                            className={`p-2 text-sm rounded-lg border transition-colors ${
                              mainLogoSize === size
                                ? 'border-pink-500 bg-pink-500/20 text-pink-300'
                                : 'border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:border-pink-500/50'
                            }`}
                            data-testid={`main-logo-size-${size}`}
                          >
                            {size === 'very_large' ? 'XL' : size.charAt(0).toUpperCase() + size.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Save Main Logo Button */}
                  <Button
                    onClick={saveMainLogo}
                    disabled={saving}
                    className="w-full mt-4 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                    data-testid="save-main-logo-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Logo Settings
                  </Button>

                  {/* Preview Buttons - in Main Logo section */}
                  <div className="mt-3 grid grid-cols-3 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          const cards = rooms.filter(r => r.tag_id);
                          const firstCard = cards.length > 0 ? cards[0] : null;
                          const loc = firstCard ? firstCard.id : 'preview';
                          window.open(`${window.location.origin}/standalone/survey/${id}/${loc}?lang=en${firstCard ? `&card=${firstCard.tag_id}` : ''}`, '_blank');
                        }}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        data-testid="preview-room-survey-main-logo-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview Room Survey
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const firstRestroom = restroomLocations.length > 0 ? restroomLocations[0] : null;
                          if (firstRestroom) {
                            window.open(`${window.location.origin}/standalone/restroom-survey/${id}/${firstRestroom.id}?lang=en`, '_blank');
                          } else {
                            toast.error('No restroom locations configured');
                          }
                        }}
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        data-testid="preview-restroom-survey-main-logo-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview Restroom Survey
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const firstRetailer = retailerAlertLocations.length > 0 ? retailerAlertLocations[0] : null;
                          if (firstRetailer) {
                            window.open(`${window.location.origin}/standalone/retailer-alert-survey/${id}/${firstRetailer.id}?lang=en`, '_blank');
                          } else {
                            toast.error('No retailer alert locations configured');
                          }
                        }}
                        className="border-fuchsia-500/30 text-fuchsia-400 hover:bg-fuchsia-500/10"
                        data-testid="preview-retailer-alert-main-logo-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview Retailer Alert
                      </Button>
                    </div>
                </div>

                <Button
                  onClick={saveHotelInfo}
                  disabled={saving}
                  className="w-full"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Property Info
                </Button>

                {/* Danger Zone - Delete Property */}
                <div className="mt-6 pt-4 border-t border-red-500/20">
                  <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-red-400">Danger Zone</p>
                        <p className="text-xs text-[hsl(215,20%,55%)] mt-1">
                          Permanently delete this property and all its data
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteDialogOpen(true)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        data-testid="delete-hotel-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Property
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </main>

      {/* Setup Wizard */}
      <SetupWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        hotel={hotel}
        orgId={id}
        onComplete={() => fetchData()}
      />

      {/* Delete Hotel Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Delete Property
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,65%)]">
              This action cannot be undone. This will permanently delete the property
              <span className="font-semibold text-white"> {hotel.name}</span> and all associated data including rooms, tags, and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-sm text-red-400">
                To confirm deletion, please type the property name:
              </p>
              <p className="font-mono text-white font-semibold mt-1">{hotel.name}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Confirm property name</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type property name to confirm"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteHotel}
              disabled={deleting || deleteConfirmText !== hotel.name}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Dialog */}
      <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle>{selectedRoom ? 'Edit Room' : 'Add Room'}</DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,65%)]">
              {selectedRoom ? 'Update room details' : 'Create a new room for guest surveys'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Room Number / Name</Label>
              <Input
                value={roomForm.identifier}
                onChange={(e) => setRoomForm({ ...roomForm, identifier: e.target.value })}
                placeholder="e.g., 101, Suite A"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="room-identifier-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Floor (optional)</Label>
              <Input
                value={roomForm.floor}
                onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}
                placeholder="e.g., 1, Ground"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label>Room Type</Label>
              <Select value={roomForm.room_type} onValueChange={(v) => setRoomForm({ ...roomForm, room_type: v })}>
                <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="deluxe">Deluxe</SelectItem>
                  <SelectItem value="suite">Suite</SelectItem>
                  <SelectItem value="penthouse">Penthouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRoomDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRoom} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {selectedRoom ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Dialog */}
      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              {selectedEmployee ? 'Edit Employee' : 'Add Employee'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Name *</Label>
              <Input
                value={employeeForm.name}
                onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                placeholder="John Smith"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="employee-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Email *</Label>
              <Input
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                placeholder="john@hotel.com"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="employee-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Phone</Label>
              <Input
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                placeholder="+1 555-123-4567"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="employee-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Role</Label>
              <Select value={employeeForm.role} onValueChange={(v) => setEmployeeForm({ ...employeeForm, role: v })}>
                <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-[hsl(215,20%,50%)]">
              Employees can be assigned to room tags to receive notifications when guests give low ratings (3 stars or less).
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveEmployee} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {selectedEmployee ? 'Update' : 'Add Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restroom Location Dialog */}
      <Dialog open={restroomLocationDialogOpen} onOpenChange={setRestroomLocationDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,14%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Droplets className="w-5 h-5 text-blue-400" />
              <span>{selectedRestroomLocation ? 'Edit Restroom Location' : 'Add Restroom Location'}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Location Name *</Label>
              <Input
                value={restroomLocationForm.name}
                onChange={(e) => setRestroomLocationForm({ ...restroomLocationForm, name: e.target.value })}
                placeholder="e.g., Main Lobby Restroom, 2nd Floor Men's"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="restroom-location-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Floor</Label>
              <Input
                value={restroomLocationForm.floor}
                onChange={(e) => setRestroomLocationForm({ ...restroomLocationForm, floor: e.target.value })}
                placeholder="e.g., 1, Ground, Basement"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="restroom-location-floor-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Building/Area</Label>
              <Input
                value={restroomLocationForm.building}
                onChange={(e) => setRestroomLocationForm({ ...restroomLocationForm, building: e.target.value })}
                placeholder="e.g., Main Building, Food Court"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="restroom-location-building-input"
              />
            </div>
            <p className="text-xs text-[hsl(215,20%,50%)]">
              Restroom locations are used for restroom surveys. Assign tags to collect cleanliness feedback.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRestroomLocationDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRestroomLocation} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {selectedRestroomLocation ? 'Update' : 'Add Location'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}

      {/* Retailer Alert Location Dialog */}
      <Dialog open={retailerAlertLocationDialogOpen} onOpenChange={setRetailerAlertLocationDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,14%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-fuchsia-400" />
              <span>{selectedRetailerAlertLocation ? 'Edit Location' : 'Add Retailer Alert Location'}</span>
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,55%)]">
              {selectedRetailerAlertLocation ? 'Update store location details.' : 'Add a new store location for retailer alerts.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Location Name *</Label>
              <Input
                placeholder="e.g., Main Entrance, Aisle 5"
                value={retailerAlertLocationForm.name}
                onChange={(e) => setRetailerAlertLocationForm({ ...retailerAlertLocationForm, name: e.target.value })}
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="retailer-alert-location-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-[hsl(215,20%,75%)]">Floor</Label>
                <Input
                  placeholder="e.g., 1, 2, B1"
                  value={retailerAlertLocationForm.floor}
                  onChange={(e) => setRetailerAlertLocationForm({ ...retailerAlertLocationForm, floor: e.target.value })}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[hsl(215,20%,75%)]">Building / Section</Label>
                <Input
                  placeholder="e.g., East Wing"
                  value={retailerAlertLocationForm.building}
                  onChange={(e) => setRetailerAlertLocationForm({ ...retailerAlertLocationForm, building: e.target.value })}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Zone Number</Label>
              <Input
                placeholder="e.g., 1, 2, 3, 4"
                value={retailerAlertLocationForm.zone_number}
                onChange={(e) => setRetailerAlertLocationForm({ ...retailerAlertLocationForm, zone_number: e.target.value })}
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="retailer-alert-location-zone-number"
              />
              <p className="text-xs text-[hsl(215,20%,45%)]">Used in incident report numbers (e.g., Z4 = Zone 4)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRetailerAlertLocationDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRetailerAlertLocation} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {selectedRetailerAlertLocation ? 'Update' : 'Add Location'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={assignUserDialogOpen} onOpenChange={setAssignUserDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,14%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <UserPlus className="w-5 h-5 text-indigo-400" />
              <span>Assign User to Property</span>
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,55%)]">
              Select a user to grant access to this property.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">User *</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" data-testid="assign-user-select">
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
                  {allUsers
                    .filter(u => !assignedUsers.some(a => a.user_id === u.id))
                    .map(u => (
                      <SelectItem key={u.id} value={u.id} className="text-white hover:bg-[hsl(217,19%,22%)]">
                        {u.name} ({u.email})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[hsl(215,20%,75%)]">Role</Label>
              <Select value={assignRole} onValueChange={setAssignRole}>
                <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" data-testid="assign-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
                  <SelectItem value="admin" className="text-white hover:bg-[hsl(217,19%,22%)]">Admin</SelectItem>
                  <SelectItem value="manager" className="text-white hover:bg-[hsl(217,19%,22%)]">Manager</SelectItem>
                  <SelectItem value="viewer" className="text-white hover:bg-[hsl(217,19%,22%)]">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={assignUser} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-assign-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Assign User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipient Add/Edit Dialog */}
      <Dialog open={recipientDialogOpen} onOpenChange={setRecipientDialogOpen}>
        <DialogContent className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="recipient-dialog-title">{editingRecipient ? 'Edit Recipient' : 'Add Recipient'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs text-[hsl(215,20%,65%)]">Recipient Name (optional)</label>
              <Input
                placeholder="e.g., Store Manager"
                value={recipientForm.name}
                onChange={(e) => setRecipientForm({...recipientForm, name: e.target.value})}
                className="bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white"
                data-testid="recipient-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs text-[hsl(215,20%,65%)]">Notification Type</label>
              <div className="flex space-x-1 bg-[hsl(217,19%,20%)] rounded-md p-0.5">
                <button
                  onClick={() => setRecipientForm({...recipientForm, type: 'email'})}
                  className={`flex-1 px-3 py-2 text-xs rounded transition-colors ${recipientForm.type === 'email' ? 'bg-blue-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                  data-testid="recipient-type-email"
                >Email</button>
                <button
                  onClick={() => setRecipientForm({...recipientForm, type: 'sms'})}
                  className={`flex-1 px-3 py-2 text-xs rounded transition-colors ${recipientForm.type === 'sms' ? 'bg-emerald-600 text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
                  data-testid="recipient-type-sms"
                >SMS</button>
              </div>
            </div>

            {recipientForm.type === 'sms' ? (
              <>
                <div className="space-y-2">
                  <label className="text-xs text-[hsl(215,20%,65%)]">Phone Number</label>
                  <Input
                    placeholder="514-555-1234"
                    value={recipientForm.phone_number}
                    onChange={(e) => setRecipientForm({...recipientForm, phone_number: e.target.value})}
                    className="bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white"
                    data-testid="recipient-phone-input"
                  />
                  <p className="text-[10px] text-[hsl(215,20%,45%)]">Accepts any format (514-555-1234, (514) 555-1234, etc.) — stored as E.164</p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[hsl(215,20%,65%)]">Carrier</label>
                  <select
                    value={recipientForm.carrier}
                    onChange={(e) => setRecipientForm({...recipientForm, carrier: e.target.value})}
                    className="w-full h-10 px-3 text-sm rounded-md bg-[hsl(217,19%,20%)] border border-[hsl(217,19%,30%)] text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="recipient-carrier-select"
                  >
                    <option value="">Select carrier...</option>
                    {carrierList.filter(c => c.country === 'CA').length > 0 && (
                      <optgroup label="Canada">
                        {carrierList.filter(c => c.country === 'CA').map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {carrierList.filter(c => c.country === 'US').length > 0 && (
                      <optgroup label="United States">
                        {carrierList.filter(c => c.country === 'US').map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <label className="text-xs text-[hsl(215,20%,65%)]">Email Address</label>
                <Input
                  placeholder="manager@store.com"
                  value={recipientForm.address}
                  onChange={(e) => setRecipientForm({...recipientForm, address: e.target.value})}
                  className="bg-[hsl(217,19%,20%)] border-[hsl(217,19%,30%)] text-white"
                  data-testid="recipient-address-input"
                />
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg">
              <div>
                <p className="text-sm text-white">Escalation Only</p>
                <p className="text-xs text-[hsl(215,20%,45%)]">Only receive alerts when primary recipients don't acknowledge</p>
              </div>
              <Switch
                checked={recipientForm.is_escalation}
                onCheckedChange={(v) => setRecipientForm({...recipientForm, is_escalation: v})}
                data-testid="recipient-escalation-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecipientDialogOpen(false)} className="border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)]">Cancel</Button>
            <Button onClick={saveRecipient} disabled={recipientSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-recipient-btn">
              {recipientSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingRecipient ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
